let sdk;

function initSdk() {
    if (!sdk) {
        sdk = {
            instance: {}
        };
    }
    return sdk;
}

function getSdk() {
    sdk = initSdk();
    if (typeof window === "undefined") {
        return sdk;
    }
    const key = "TracerSDK_2_1_x";
    if (!window[key]) {
        window[key] = sdk;
    }
    return window[key];
}

function getSdkInstance(sdkKey) {
    const sdk = getSdk();
    let instance = sdk.instance[sdkKey];
    if (!instance) {
        instance = {
            inner: {},
            modules: {},
            version: "2.3.1"
        };
        sdk.instance[sdkKey] = instance;
    }
    return instance;
}

function getSdkInnerModules(sdkKey) {
    return getSdkInstance(sdkKey).inner;
}

function initSdkInnerModule(sdkKey, moduleKey, init) {
    const data = getSdkInnerModules(sdkKey);
    if (!data[moduleKey]) {
        data[moduleKey] = init(sdkKey);
    }
    return data[moduleKey];
}

class LogsData {
    constructor() {
        this.log = [];
        this.logBytes = [];
        this.logIndex = 0;
        this.logTotalBytes = 0;
        this.logMaxBytes = 64e3;
    }
    destroy() {
        this.clear();
    }
    add(message) {
        const logRow = `#${this.logIndex} ${Date.now()} | ${message}\n`;
        const logRowBytes = new Blob([ logRow ]).size;
        this.logTotalBytes += logRowBytes;
        this.log.push(logRow);
        this.logBytes.push(logRowBytes);
        this.logIndex++;
        if (this.logTotalBytes > this.logMaxBytes) {
            const outOfLimitBytes = this.logTotalBytes - this.logMaxBytes;
            let bytesCount = 0;
            const ln = this.logBytes.length - 1;
            for (let i = 0; i < ln; i++) {
                bytesCount += this.logBytes[i];
                if (bytesCount >= outOfLimitBytes || i == ln - 1) {
                    this.logBytes = this.logBytes.slice(i + 1);
                    this.log = this.log.slice(i + 1);
                    this.logTotalBytes -= bytesCount;
                    break;
                }
            }
        }
    }
    getData() {
        return this.log;
    }
    clear() {
        this.log = [];
        this.logBytes = [];
        this.logTotalBytes = 0;
    }
}

function getSdkPublicModules(sdkKey) {
    return getSdkInstance(sdkKey).modules;
}

function initSdkPublicModule(sdkKey, moduleKey, init) {
    const data = getSdkPublicModules(sdkKey);
    if (!data[moduleKey]) {
        data[moduleKey] = init(sdkKey);
    } else {
        throw new Error(`Tracer module "${moduleKey}" was already initialized`);
    }
    return data[moduleKey];
}

function getSdkInnerModule(sdkKey, moduleKey) {
    return getSdkInnerModules(sdkKey)[moduleKey];
}

class TracerLog {
    constructor(sdkKey) {
        this.sdkKey = sdkKey;
    }
    getSdkData() {
        return getSdkInnerModule(this.sdkKey, 0);
    }
    log(message) {
        const data = this.getSdkData();
        if (data) {
            data.add(message);
        }
    }
    clearLog() {
        const data = this.getSdkData();
        if (data) {
            data.clear();
        }
    }
}

function innerInitTracerLog(sdkKey) {
    initSdkInnerModule(sdkKey, 0, (function() {
        return new LogsData;
    }));
    return initSdkPublicModule(sdkKey, "log", (function() {
        return new TracerLog(sdkKey);
    }));
}

const mainSdkKey = "tracerMain";

function initTracerLog() {
    return innerInitTracerLog(mainSdkKey);
}

function isSeverityCritical(severity) {
    return severity === "fatal";
}

const validSeverities = [ "fatal", "error", "warning", "notice", "debug" ];

function getSeverityIndex(severity) {
    return severity ? validSeverities.indexOf(severity) : -1;
}

function getMostCriticalSeverity(...severities) {
    let minValidIndex = null;
    for (let i = 0; i < severities.length; i++) {
        const severityIndex = getSeverityIndex(severities[i]);
        if (severityIndex !== -1) {
            if (minValidIndex === null || severityIndex < minValidIndex) {
                minValidIndex = severityIndex;
            }
        }
    }
    return minValidIndex === null ? undefined : validSeverities[minValidIndex];
}

class ErrorsData {
    constructor(sdkKey, config) {
        this.crashesCount = 0;
        this.errors = [];
        this.keys = {};
        this.sdkKey = sdkKey;
        this.config = config;
    }
    getConfig() {
        return this.config;
    }
    withCrashes() {
        return Boolean(this.crashesCount);
    }
    getMaxSeverity() {
        return this.maxSeverity;
    }
    destroy() {
        this.errors = [];
        this.keys = {};
        this.userId = undefined;
        this.module = undefined;
    }
    add(error) {
        this.maxSeverity = getMostCriticalSeverity(this.maxSeverity, error.severity);
        const criticalError = isSeverityCritical(error.severity) || error.type === "CRASH";
        if (criticalError) {
            this.crashesCount++;
            this.errors.push(error);
            const sessionUploader = getSdkInnerModule(this.sdkKey, 8);
            const errorsUploader = getSdkInnerModule(this.sdkKey, 3);
            if (sessionUploader) {
                sessionUploader.setCrashed(this.maxSeverity);
            }
            if (errorsUploader && this.crashesCount === 1) {
                errorsUploader.forceUpload();
            }
        } else {
            this.errors.push(error);
        }
    }
    getData() {
        return this.errors;
    }
    dequeue(count = 1) {
        return this.errors.splice(0, count);
    }
    setKey(key, value) {
        this.keys[key] = value;
    }
    setKeys(keys) {
        for (const key in keys) {
            if (Object.prototype.hasOwnProperty.call(keys, key)) {
                this.keys[key] = keys[key];
            }
        }
    }
    removeKey(key) {
        delete this.keys[key];
    }
    removeKeys(keys) {
        for (let i = 0, ln = keys.length; i < ln; i++) {
            this.removeKey(keys[i]);
        }
    }
    getKeys() {
        return this.keys;
    }
}

function getCleanErrorStack(errorName, errorMessage, stack, removeFirstLines, removeLastLines) {
    const cutStr = errorName + ": " + errorMessage;
    stack = stack || "";
    const cutMatchIndex = stack.indexOf(cutStr);
    if (cutMatchIndex === 0) {
        stack = stack.slice(cutStr.length + 1);
    }
    if (removeFirstLines || removeLastLines) {
        const lines = stack.split("\n");
        const from = removeFirstLines || 0;
        const to = removeLastLines ? lines.length - removeLastLines : lines.length;
        stack = lines.slice(from, to).join("\n");
    }
    return stack;
}

const tracerCurrentStackKey = "tracerCurrentStack";

let genericError;

function getGenericError() {
    if (!genericError) {
        genericError = new Error(tracerCurrentStackKey);
    }
    return genericError;
}

function startScope(scope) {
    getSdk().currentAsyncScope = limitScopeDepth(scope, 20);
}

function limitScopeDepth(scope, count) {
    if (!scope) {
        return;
    }
    if (count === 0) {
        scope.parent = undefined;
    }
    limitScopeDepth(scope.parent, count - 1);
    return scope;
}

function endScope() {
    const currentScope = getCurrentScope();
    if (!currentScope) {
        return;
    }
    currentScope.sameScopeCount--;
    getSdk().currentAsyncScope = findActiveParentScope(currentScope);
}

function findActiveParentScope(scope) {
    if (!scope) {
        return;
    }
    return scope.sameScopeCount ? scope : findActiveParentScope(scope.parent);
}

function clearScope() {
    getSdk().currentAsyncScope = undefined;
}

function getCurrentScope() {
    return getSdk().currentAsyncScope;
}

function getScopeStack(scope, stacks = [], cleanup) {
    let delimiter = "(" + scope.name + ")";
    if (scope.sameScopeCount > 1) {
        delimiter += " x" + scope.sameScopeCount;
    }
    const isLast = !scope.parent;
    stacks.push(delimiter);
    if (cleanup) {
        const genericError = getGenericError();
        stacks.push(getCleanErrorStack(genericError.name, genericError.message, scope.stack, 3, isLast ? 0 : 1));
    } else {
        stacks.push(scope.stack);
    }
    if (scope.parent) {
        getScopeStack(scope.parent, stacks, cleanup);
    }
    return stacks.join("\n");
}

function getDisableStackCleanup(sdkKey) {
    const asyncData = getSdkInnerModule(sdkKey, 4);
    return asyncData ? asyncData.disableStackCleanup : false;
}

function getAsyncErrorStack(sdkKey, error, scope) {
    scope = error.tracerScope || scope;
    if (scope) {
        const disableStackCleanup = getDisableStackCleanup(sdkKey);
        return getScopeStack(scope, [ getCleanErrorStack(error.name, error.message, error.stack, 0, disableStackCleanup ? 0 : 1) ], !disableStackCleanup);
    }
    return getCleanErrorStack(error.name, error.message, error.stack);
}

const featureMutedToMap = {};

class LocalStorage {
    constructor() {
        this.data = {};
    }
    getItem(key) {
        return this.data[key] || null;
    }
    removeItem(key) {
        this.data[key] = undefined;
    }
    setItem(key, value) {
        this.data[key] = value;
    }
}

let ls;

function getLocalStorage() {
    if (!ls) {
        const sdk = getSdk();
        ls = sdk.ls;
        if (!ls) {
            ls = getLocalStorageSafe();
            sdk.ls = ls;
        }
    }
    return ls;
}

function getLocalStorageSafe() {
    let ls;
    try {
        if (typeof window === "undefined") {
            return new LocalStorage;
        }
        ls = window["localStorage"];
        const lsTestKey = "__ls_tracer_test";
        ls.setItem(lsTestKey, lsTestKey);
        if (ls.getItem(lsTestKey) !== lsTestKey) {
            return new LocalStorage;
        }
        ls.removeItem(lsTestKey);
        return ls;
    } catch (e) {
        return new LocalStorage;
    }
}

function getFeatureMuteLsKey(feature) {
    return "tracer-mute-" + feature;
}

function getFeatureMutedFromLs(feature) {
    const mutedTo = getLocalStorage().getItem(getFeatureMuteLsKey(feature));
    return mutedTo ? parseInt(mutedTo) : null;
}

function unmuteApiFeature(feature) {
    featureMutedToMap[feature] = null;
    getLocalStorage().removeItem(getFeatureMuteLsKey(feature));
}

function isFeatureMuted(feature) {
    if (feature !== "all" && checkFeatureMuted("all")) {
        return true;
    }
    return checkFeatureMuted(feature);
}

function checkFeatureMuted(feature) {
    let mutedTo = featureMutedToMap[feature];
    if (mutedTo === undefined) {
        featureMutedToMap[feature] = mutedTo = getFeatureMutedFromLs(feature);
    }
    if (!mutedTo) {
        return false;
    }
    if (Date.now() < mutedTo) {
        return true;
    }
    unmuteApiFeature(feature);
    return false;
}

function getVisibilityState() {
    try {
        return String(document.visibilityState);
    } catch (e) {
        return "unknown";
    }
}

function cyrb53(str, seed = 0) {
    let h1 = 3735928559 ^ seed, h2 = 1103547991 ^ seed;
    for (let i = 0, ch; i < str.length; i++) {
        ch = str.charCodeAt(i);
        h1 = Math.imul(h1 ^ ch, 2654435761);
        h2 = Math.imul(h2 ^ ch, 1597334677);
    }
    h1 = Math.imul(h1 ^ h1 >>> 16, 2246822507);
    h1 ^= Math.imul(h2 ^ h2 >>> 13, 3266489909);
    h2 = Math.imul(h2 ^ h2 >>> 16, 2246822507);
    h2 ^= Math.imul(h1 ^ h1 >>> 13, 3266489909);
    return 4294967296 * (2097151 & h2) + (h1 >>> 0);
}

const hashTimeMap = {};

const THROTTLE_INTERVAL_MS = 1e4;

function throttleError(errorText) {
    const hash = cyrb53(errorText);
    const lastTime = hashTimeMap[hash];
    const nowTime = Date.now();
    if (lastTime && nowTime - lastTime <= THROTTLE_INTERVAL_MS) {
        return true;
    }
    hashTimeMap[hash] = nowTime;
    return false;
}

function addCauseErrorToStack(sdkKey, cause, stack) {
    const causeName = cause.name;
    const causeMessage = cause.message;
    const causedBy = causeName + ": " + causeMessage + "\n" + getAsyncErrorStack(sdkKey, cause);
    return stack + "\n\n" + "Caused by: " + causedBy;
}

function isError(value) {
    return value instanceof Error;
}

function isObject(value) {
    return typeof value === "object";
}

function isSeverityValid(severity) {
    if (!severity) {
        return false;
    }
    if (validSeverities.indexOf(severity) === -1) {
        console.warn("Wrong Tracer severity, value will be ignored. Valid values: " + validSeverities.join(", "));
        return false;
    }
    return true;
}

function isFunction(value) {
    return typeof value === "function";
}

function handleError(sdkKey, errorData) {
    if (isFeatureMuted("crashUpload")) {
        return;
    }
    const errorsData = getSdkInnerModule(sdkKey, 1);
    const logsData = getSdkInnerModule(sdkKey, 0);
    if (!errorsData) {
        return;
    }
    let errorStack = getAsyncErrorStack(sdkKey, errorData.error, errorData.asyncScope);
    const cause = errorData.error.cause;
    let causeSimpleValue;
    if (cause !== undefined) {
        try {
            if (isError(cause)) {
                errorStack = addCauseErrorToStack(sdkKey, cause, errorStack);
            } else {
                const objectToString = Object.prototype.toString;
                if (isObject(cause)) {
                    causeSimpleValue = objectToString.call(cause);
                    try {
                        if (errorsData.getConfig().stringifyErrorDataObject) {
                            causeSimpleValue = JSON.stringify(cause);
                        }
                    } catch (e) {}
                } else if (isFunction(cause)) {
                    causeSimpleValue = objectToString.call(cause);
                } else {
                    causeSimpleValue = String(cause);
                }
            }
        } catch (e) {}
    }
    const data = Object.assign(Object.assign({}, errorData), {
        timestamp: Date.now(),
        log: logsData ? logsData.getData().join("") : undefined,
        errorStack: errorStack,
        causeSimpleValue: causeSimpleValue ? causeSimpleValue.slice(0, 50) : undefined,
        documentVisibilityState: getVisibilityState(),
        severity: isSeverityValid(errorData.severity) ? errorData.severity : undefined
    });
    data.keys = Object.assign(Object.assign({}, errorsData.getKeys()), data.keys);
    if (!data.userId) {
        data.userId = errorsData.userId;
    }
    if (!data.module) {
        data.module = errorsData.module;
    }
    const filterError = errorsData.getConfig().filterError;
    if (filterError) {
        const errorInfo = {
            severity: data.severity,
            keys: JSON.parse(JSON.stringify(data.keys)),
            issueKey: data.issueKey,
            crashIdSource: data.crashIdSource,
            userId: data.userId,
            component: data.component,
            versionName: data.versionName,
            versionCode: data.versionCode,
            errorStack: errorStack,
            causeSimpleValue: causeSimpleValue
        };
        if (filterError(errorData.error, errorInfo) === false) {
            return;
        }
    }
    if (throttleError(errorData.crashIdSource || errorStack || errorData.error.name || errorData.error.message)) {
        return;
    }
    errorsData.add(data);
}

let windowListenersInited = false;

function windowErrorHandler(sdkKey, restrictOtherErrorListeners = false, handleNotErrorData = false) {
    if (typeof window === "undefined" || windowListenersInited) {
        return {
            destroy: function() {}
        };
    }
    window.addEventListener("error", onWindowError);
    window.addEventListener("unhandledrejection", onWindowUnhandledRejection);
    windowListenersInited = true;
    function onWindowError(e) {
        if (restrictOtherErrorListeners) {
            e.stopImmediatePropagation();
        }
        handleCrash(sdkKey, e.error, e.type, handleNotErrorData);
    }
    function onWindowUnhandledRejection(e) {
        if (restrictOtherErrorListeners) {
            e.stopImmediatePropagation();
        }
        handleCrash(sdkKey, e.reason, e.type, handleNotErrorData);
    }
    return {
        destroy: function() {
            if (windowListenersInited) {
                window.removeEventListener("error", onWindowError);
                window.removeEventListener("unhandledrejection", onWindowUnhandledRejection);
            }
        }
    };
}

function handleCrash(sdkKey, e, errorEventType, handleNotErrorData) {
    if (isError(e)) {
        handleError(sdkKey, {
            error: e,
            type: "CRASH",
            errorEventType: errorEventType
        });
    } else if (handleNotErrorData) {
        const error = new Error("Unhandled data error");
        error.cause = e;
        handleError(sdkKey, {
            error: error,
            type: "CRASH",
            errorEventType: errorEventType
        });
    }
}

class TracerError {
    constructor(sdkKey) {
        this.sdkKey = sdkKey;
    }
    getErrorsData() {
        return getSdkInnerModule(this.sdkKey, 1);
    }
    error(error, data) {
        let errorInstance;
        if (isError(error)) {
            errorInstance = error;
        } else {
            errorInstance = new Error("Unknown data error");
            errorInstance.cause = error;
            errorInstance.stack = getCleanErrorStack(errorInstance.name, errorInstance.message, errorInstance.stack, 1);
        }
        handleError(this.sdkKey, Object.assign(Object.assign({}, data), {
            type: "NON_FATAL",
            error: errorInstance,
            errorEventType: "manual",
            asyncScope: errorInstance.tracerScope ? undefined : getCurrentScope()
        }));
    }
    setErrorKey(key, value) {
        const data = this.getErrorsData();
        if (data) {
            data.setKey(key, value);
        }
    }
    setErrorKeys(keys) {
        const data = this.getErrorsData();
        if (data) {
            data.setKeys(keys);
        }
    }
    removeErrorKey(key) {
        const data = this.getErrorsData();
        if (data) {
            data.removeKey(key);
        }
    }
    removeErrorKeys(keys) {
        const data = this.getErrorsData();
        if (data) {
            data.removeKeys(keys);
        }
    }
    setUserId(id) {
        const data = this.getErrorsData();
        if (data) {
            data.userId = id;
        }
    }
    removeUserId() {
        const data = this.getErrorsData();
        if (data) {
            data.userId = undefined;
        }
    }
    setModule(name) {
        const data = this.getErrorsData();
        if (data) {
            data.module = name;
        }
    }
    removeModule() {
        const data = this.getErrorsData();
        if (data) {
            data.module = undefined;
        }
    }
}

function innerInitTracerError(sdkKey, config) {
    Error.stackTraceLimit = config.stackTraceLimit || 50;
    initSdkInnerModule(sdkKey, 1, (function() {
        return new ErrorsData(sdkKey, config);
    }));
    if (!config.disableUnhandledErrorsListeners) {
        initSdkInnerModule(sdkKey, 2, (function() {
            return windowErrorHandler(sdkKey, config.restrictOtherErrorListeners, config.handleOtherErrorDataType);
        }));
    }
    return initSdkPublicModule(sdkKey, "error", (function() {
        return new TracerError(sdkKey);
    }));
}

function initTracerError(config = {}) {
    return innerInitTracerError(mainSdkKey, {
        stackTraceLimit: config.stackTraceLimit,
        disableUnhandledErrorsListeners: config.disableUnhandledErrorsListeners,
        restrictOtherErrorListeners: config.restrictOtherErrorListeners,
        filterError: config.filterError,
        handleOtherErrorDataType: config.handleOtherErrorDataType,
        stringifyErrorDataObject: config.stringifyErrorDataObject
    });
}

function uuidv4() {
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (function(c) {
        const r = Math.random() * 16 | 0;
        const v = c == "x" ? r : r & 3 | 8;
        return v.toString(16);
    }));
}

const deviceIdLsKey = "tracer-device-id";

function getDeviceId() {
    const ls = getLocalStorage();
    let id = ls.getItem(deviceIdLsKey);
    if (!id) {
        id = uuidv4();
        ls.setItem(deviceIdLsKey, id);
    }
    return id;
}

function rfc3339(d) {
    return d.getFullYear() + "-" + pad(d.getMonth() + 1) + "-" + pad(d.getDate()) + "T" + pad(d.getHours()) + ":" + pad(d.getMinutes()) + ":" + pad(d.getSeconds()) + timezoneOffset(d.getTimezoneOffset());
}

function pad(n) {
    return n < 10 ? "0" + n : n;
}

function timezoneOffset(offset) {
    if (offset === 0) {
        return "Z";
    }
    const sign = offset > 0 ? "-" : "+";
    offset = Math.abs(offset);
    return sign + pad(Math.floor(offset / 60)) + ":" + pad(offset % 60);
}

const windowDefined = typeof window !== "undefined";

function isWindowDefined() {
    return windowDefined;
}

function canGzip() {
    return isWindowDefined() ? Boolean(window.CompressionStream) : false;
}

function gzip(str, onResult, onError) {
    const blob = new Blob([ str ]);
    if (!canGzip()) {
        onError(blob);
        return;
    }
    try {
        const reader = blob.stream().pipeThrough(new CompressionStream("gzip")).getReader();
        readStream(reader).then(onResult, (function() {
            onError(blob);
        }));
    } catch (e) {
        onError(blob);
    }
}

function readStream(reader, out = []) {
    return reader.read().then((function(result) {
        if (result.value) {
            out.push(result.value);
        }
        if (!result.done) {
            return readStream(reader, out);
        } else {
            return new Blob(out);
        }
    }));
}

function getSessionUuid() {
    const sdk = getSdk();
    if (!sdk.sessionUuid) {
        sdk.sessionUuid = uuidv4();
    }
    return sdk.sessionUuid;
}

let platformResult;

function getUserAgentData() {
    if (platformResult) {
        return platformResult;
    }
    const objectTypes = {
        function: true,
        object: true
    };
    let root = objectTypes[typeof window] && window || this;
    const freeExports = objectTypes[typeof exports] && exports;
    const freeModule = objectTypes[typeof module] && module && !module.nodeType && module;
    const freeGlobal = freeExports && freeModule && typeof global == "object" && global;
    if (freeGlobal && (freeGlobal.global === freeGlobal || freeGlobal.window === freeGlobal || freeGlobal.self === freeGlobal)) {
        root = freeGlobal;
    }
    const maxSafeInteger = Math.pow(2, 53) - 1;
    const reOpera = /\bOpera/;
    const objectProto = Object.prototype;
    const hasOwnProperty = objectProto.hasOwnProperty;
    const toString = objectProto.toString;
    function capitalize(string) {
        string = String(string);
        return string.charAt(0).toUpperCase() + string.slice(1);
    }
    function cleanupOS(os, pattern, label) {
        let data = {
            "10.0": "10",
            6.4: "10 Technical Preview",
            6.3: "8.1",
            6.2: "8",
            6.1: "Server 2008 R2 / 7",
            "6.0": "Server 2008 / Vista",
            5.2: "Server 2003 / XP 64-bit",
            5.1: "XP",
            5.01: "2000 SP1",
            "5.0": "2000",
            "4.0": "NT",
            "4.90": "ME"
        };
        if (pattern && label && /^Win/i.test(os) && !/^Windows Phone /i.test(os) && (data = data[/[\d.]+$/.exec(os)])) {
            os = "Windows " + data;
        }
        os = String(os);
        if (pattern && label) {
            os = os.replace(RegExp(pattern, "i"), label);
        }
        os = format(os.replace(/ ce$/i, " CE").replace(/\bhpw/i, "web").replace(/\bMacintosh\b/, "Mac OS").replace(/_PowerPC\b/i, " OS").replace(/\b(OS X) [^ \d]+/i, "$1").replace(/\bMac (OS X)\b/, "$1").replace(/\/(\d)/, " $1").replace(/_/g, ".").replace(/(?: BePC|[ .]*fc[ \d.]+)$/i, "").replace(/\bx86\.64\b/gi, "x86_64").replace(/\b(Windows Phone) OS\b/, "$1").replace(/\b(Chrome OS \w+) [\d.]+\b/, "$1").split(" on ")[0]);
        return os;
    }
    function each(object, callback) {
        let index = -1, length = object ? object.length : 0;
        if (typeof length == "number" && length > -1 && length <= maxSafeInteger) {
            while (++index < length) {
                callback(object[index], index, object);
            }
        } else {
            forOwn(object, callback);
        }
    }
    function format(string) {
        string = trim(string);
        return /^(?:webOS|i(?:OS|P))/.test(string) ? string : capitalize(string);
    }
    function forOwn(object, callback) {
        for (const key in object) {
            if (hasOwnProperty.call(object, key)) {
                callback(object[key], key, object);
            }
        }
    }
    function getClassOf(value) {
        return value == null ? capitalize(value) : toString.call(value).slice(8, -1);
    }
    function isHostType(object, property) {
        const type = object != null ? typeof object[property] : "number";
        return !/^(?:boolean|number|string|undefined)$/.test(type) && (type == "object" ? !!object[property] : true);
    }
    function qualify(string) {
        return String(string).replace(/([ -])(?!$)/g, "$1?");
    }
    function reduce(array, callback) {
        let accumulator = null;
        each(array, (function(value, index) {
            accumulator = callback(accumulator, value, index, array);
        }));
        return accumulator;
    }
    function trim(string) {
        return String(string).replace(/^ +| +$/g, "");
    }
    function parse(ua) {
        let context = root;
        const isCustomContext = ua && typeof ua == "object" && getClassOf(ua) != "String";
        if (isCustomContext) {
            context = ua;
            ua = null;
        }
        const nav = context.navigator || {};
        const userAgent = nav.userAgent || "";
        ua || (ua = userAgent);
        const likeChrome = isCustomContext ? !!nav.likeChrome : /\bChrome\b/.test(ua) && !/internal|\n/i.test(toString.toString());
        const objectClass = "Object", airRuntimeClass = isCustomContext ? objectClass : "ScriptBridgingProxyObject", enviroClass = isCustomContext ? objectClass : "Environment", javaClass = isCustomContext && context.java ? "JavaPackage" : getClassOf(context.java), phantomClass = isCustomContext ? objectClass : "RuntimeObject";
        const java = /\bJava/.test(javaClass) && context.java;
        const rhino = java && getClassOf(context.environment) == enviroClass;
        const alpha = java ? "a" : "α";
        const beta = java ? "b" : "β";
        const doc = context.document || {};
        let opera = context.operamini || context.opera;
        var operaClass = reOpera.test(operaClass = isCustomContext && opera ? opera["[[Class]]"] : getClassOf(opera)) ? operaClass : opera = null;
        let data;
        let arch = ua;
        let description = [];
        let prerelease = null;
        const useFeatures = ua == userAgent;
        let version = useFeatures && opera && typeof opera.version == "function" && opera.version();
        let isSpecialCasedOS;
        let layout = getLayout([ {
            label: "EdgeHTML",
            pattern: "Edge"
        }, "Trident", {
            label: "WebKit",
            pattern: "AppleWebKit"
        }, "iCab", "Presto", "NetFront", "Tasman", "KHTML", "Gecko" ]);
        let name = getName([ "Adobe AIR", "Arora", "Avant Browser", "Breach", "Camino", "Electron", "Epiphany", "Fennec", "Flock", "Galeon", "GreenBrowser", "iCab", "Iceweasel", "K-Meleon", "Konqueror", "Lunascape", "Maxthon", {
            label: "Microsoft Edge",
            pattern: "(?:Edge|Edg|EdgA|EdgiOS)"
        }, "Midori", "Nook Browser", "PaleMoon", "PhantomJS", "Raven", "Rekonq", "RockMelt", {
            label: "Samsung Internet",
            pattern: "SamsungBrowser"
        }, "SeaMonkey", {
            label: "Silk",
            pattern: "(?:Cloud9|Silk-Accelerated)"
        }, "Sleipnir", "SlimBrowser", {
            label: "SRWare Iron",
            pattern: "Iron"
        }, "Sunrise", "Swiftfox", "Vivaldi", "Waterfox", "WebPositive", {
            label: "Yandex Browser",
            pattern: "YaBrowser"
        }, {
            label: "UC Browser",
            pattern: "UCBrowser"
        }, "Opera Mini", {
            label: "Opera Mini",
            pattern: "OPiOS"
        }, "Opera", {
            label: "Opera",
            pattern: "OPR"
        }, "Chromium", "Chrome", {
            label: "Chrome",
            pattern: "(?:HeadlessChrome)"
        }, {
            label: "Chrome Mobile",
            pattern: "(?:CriOS|CrMo)"
        }, {
            label: "Firefox",
            pattern: "(?:Firefox|Minefield)"
        }, {
            label: "Firefox for iOS",
            pattern: "FxiOS"
        }, {
            label: "IE",
            pattern: "IEMobile"
        }, {
            label: "IE",
            pattern: "MSIE"
        }, "Safari" ]);
        let product = getProduct([ {
            label: "BlackBerry",
            pattern: "BB10"
        }, "BlackBerry", {
            label: "Galaxy S",
            pattern: "GT-I9000"
        }, {
            label: "Galaxy S2",
            pattern: "GT-I9100"
        }, {
            label: "Galaxy S3",
            pattern: "GT-I9300"
        }, {
            label: "Galaxy S4",
            pattern: "GT-I9500"
        }, {
            label: "Galaxy S5",
            pattern: "SM-G900"
        }, {
            label: "Galaxy S6",
            pattern: "SM-G920"
        }, {
            label: "Galaxy S6 Edge",
            pattern: "SM-G925"
        }, {
            label: "Galaxy S7",
            pattern: "SM-G930"
        }, {
            label: "Galaxy S7 Edge",
            pattern: "SM-G935"
        }, "Google TV", "Lumia", "iPad", "iPod", "iPhone", "Kindle", {
            label: "Kindle Fire",
            pattern: "(?:Cloud9|Silk-Accelerated)"
        }, "Nexus", "Nook", "PlayBook", "PlayStation Vita", "PlayStation", "TouchPad", "Transformer", {
            label: "Wii U",
            pattern: "WiiU"
        }, "Wii", "Xbox One", {
            label: "Xbox 360",
            pattern: "Xbox"
        }, "Xoom" ]);
        let manufacturer = getManufacturer({
            Apple: {
                iPad: 1,
                iPhone: 1,
                iPod: 1
            },
            Alcatel: {},
            Archos: {},
            Amazon: {
                Kindle: 1,
                "Kindle Fire": 1
            },
            Asus: {
                Transformer: 1
            },
            "Barnes & Noble": {
                Nook: 1
            },
            BlackBerry: {
                PlayBook: 1
            },
            Google: {
                "Google TV": 1,
                Nexus: 1
            },
            HP: {
                TouchPad: 1
            },
            HTC: {},
            Huawei: {},
            Lenovo: {},
            LG: {},
            Microsoft: {
                Xbox: 1,
                "Xbox One": 1
            },
            Motorola: {
                Xoom: 1
            },
            Nintendo: {
                "Wii U": 1,
                Wii: 1
            },
            Nokia: {
                Lumia: 1
            },
            Oppo: {},
            Samsung: {
                "Galaxy S": 1,
                "Galaxy S2": 1,
                "Galaxy S3": 1,
                "Galaxy S4": 1
            },
            Sony: {
                PlayStation: 1,
                "PlayStation Vita": 1
            },
            Xiaomi: {
                Mi: 1,
                Redmi: 1
            }
        });
        let os = getOS([ "Windows Phone", "KaiOS", "Android", "CentOS", {
            label: "Chrome OS",
            pattern: "CrOS"
        }, "Debian", {
            label: "DragonFly BSD",
            pattern: "DragonFly"
        }, "Fedora", "FreeBSD", "Gentoo", "Haiku", "Kubuntu", "Linux Mint", "OpenBSD", "Red Hat", "SuSE", "Ubuntu", "Xubuntu", "Cygwin", "Symbian OS", "hpwOS", "webOS ", "webOS", "Tablet OS", "Tizen", "Linux", "Mac OS X", "Macintosh", "Mac", "Windows 98;", "Windows " ]);
        function getLayout(guesses) {
            return reduce(guesses, (function(result, guess) {
                return result || RegExp("\\b" + (guess.pattern || qualify(guess)) + "\\b", "i").exec(ua) && (guess.label || guess);
            }));
        }
        function getManufacturer(guesses) {
            return reduce(guesses, (function(result, value, key) {
                return result || (value[product] || value[/^[a-z]+(?: +[a-z]+\b)*/i.exec(product)] || RegExp("\\b" + qualify(key) + "(?:\\b|\\w*\\d)", "i").exec(ua)) && key;
            }));
        }
        function getName(guesses) {
            return reduce(guesses, (function(result, guess) {
                return result || RegExp("\\b" + (guess.pattern || qualify(guess)) + "\\b", "i").exec(ua) && (guess.label || guess);
            }));
        }
        function getOS(guesses) {
            return reduce(guesses, (function(result, guess) {
                const pattern = guess.pattern || qualify(guess);
                if (!result && (result = RegExp("\\b" + pattern + "(?:/[\\d.]+|[ \\w.]*)", "i").exec(ua))) {
                    result = cleanupOS(result, pattern, guess.label || guess);
                }
                return result;
            }));
        }
        function getProduct(guesses) {
            return reduce(guesses, (function(result, guess) {
                const pattern = guess.pattern || qualify(guess);
                if (!result && (result = RegExp("\\b" + pattern + " *\\d+[.\\w_]*", "i").exec(ua) || RegExp("\\b" + pattern + " *\\w+-[\\w]*", "i").exec(ua) || RegExp("\\b" + pattern + "(?:; *(?:[a-z]+[_-])?[a-z]+\\d+|[^ ();-]*)", "i").exec(ua))) {
                    if ((result = String(guess.label && !RegExp(pattern, "i").test(guess.label) ? guess.label : result).split("/"))[1] && !/[\d.]+/.test(result[0])) {
                        result[0] += " " + result[1];
                    }
                    guess = guess.label || guess;
                    result = format(result[0].replace(RegExp(pattern, "i"), guess).replace(RegExp("; *(?:" + guess + "[_-])?", "i"), " ").replace(RegExp("(" + guess + ")[-_.]?(\\w)", "i"), "$1 $2"));
                }
                return result;
            }));
        }
        function getVersion(patterns) {
            return reduce(patterns, (function(result, pattern) {
                return result || (RegExp(pattern + "(?:-[\\d.]+/|(?: for [\\w-]+)?[ /-])([\\d.]+[^ ();/_-]*)", "i").exec(ua) || 0)[1] || null;
            }));
        }
        function toStringPlatform() {
            return this.description || "";
        }
        layout && (layout = [ layout ]);
        if (/\bAndroid\b/.test(os) && !product && (data = /\bAndroid[^;]*;(.*?)(?:Build|\) AppleWebKit)\b/i.exec(ua))) {
            product = trim(data[1]).replace(/^[a-z]{2}-[a-z]{2};\s*/i, "") || null;
        }
        if (manufacturer && !product) {
            product = getProduct([ manufacturer ]);
        } else if (manufacturer && product) {
            product = product.replace(RegExp("^(" + qualify(manufacturer) + ")[-_.\\s]", "i"), manufacturer + " ").replace(RegExp("^(" + qualify(manufacturer) + ")[-_.]?(\\w)", "i"), manufacturer + " $2");
        }
        if (data = /\bGoogle TV\b/.exec(product)) {
            product = data[0];
        }
        if (/\bSimulator\b/i.test(ua)) {
            product = (product ? product + " " : "") + "Simulator";
        }
        if (name == "Opera Mini" && /\bOPiOS\b/.test(ua)) {
            description.push("running in Turbo/Uncompressed mode");
        }
        if (name == "IE" && /\blike iPhone OS\b/.test(ua)) {
            data = parse(ua.replace(/like iPhone OS/, ""));
            manufacturer = data.manufacturer;
            product = data.product;
        } else if (/^iP/.test(product)) {
            name || (name = "Safari");
            os = "iOS" + ((data = / OS ([\d_]+)/i.exec(ua)) ? " " + data[1].replace(/_/g, ".") : "");
        } else if (name == "Konqueror" && /^Linux\b/i.test(os)) {
            os = "Kubuntu";
        } else if (manufacturer && manufacturer != "Google" && (/Chrome/.test(name) && !/\bMobile Safari\b/i.test(ua) || /\bVita\b/.test(product)) || /\bAndroid\b/.test(os) && /^Chrome/.test(name) && /\bVersion\//i.test(ua)) {
            name = "Android Browser";
            os = /\bAndroid\b/.test(os) ? os : "Android";
        } else if (name == "Silk") {
            if (!/\bMobi/i.test(ua)) {
                os = "Android";
                description.unshift("desktop mode");
            }
            if (/Accelerated *= *true/i.test(ua)) {
                description.unshift("accelerated");
            }
        } else if (name == "UC Browser" && /\bUCWEB\b/.test(ua)) {
            description.push("speed mode");
        } else if (name == "PaleMoon" && (data = /\bFirefox\/([\d.]+)\b/.exec(ua))) {
            description.push("identifying as Firefox " + data[1]);
        } else if (name == "Firefox" && (data = /\b(Mobile|Tablet|TV)\b/i.exec(ua))) {
            os || (os = "Firefox OS");
            product || (product = data[1]);
        } else if (!name || (data = !/\bMinefield\b/i.test(ua) && /\b(?:Firefox|Safari)\b/.exec(name))) {
            if (name && !product && /[\/,]|^[^(]+?\)/.test(ua.slice(ua.indexOf(data + "/") + 8))) {
                name = null;
            }
            if ((data = product || manufacturer || os) && (product || manufacturer || /\b(?:Android|Symbian OS|Tablet OS|webOS)\b/.test(os))) {
                name = /[a-z]+(?: Hat)?/i.exec(/\bAndroid\b/.test(os) ? os : data) + " Browser";
            }
        } else if (name == "Electron" && (data = (/\bChrome\/([\d.]+)\b/.exec(ua) || 0)[1])) {
            description.push("Chromium " + data);
        }
        if (!version) {
            version = getVersion([ "(?:Cloud9|CriOS|CrMo|Edge|Edg|EdgA|EdgiOS|FxiOS|HeadlessChrome|IEMobile|Iron|Opera ?Mini|OPiOS|OPR|Raven|SamsungBrowser|Silk(?!/[\\d.]+$)|UCBrowser|YaBrowser)", "Version", qualify(name), "(?:Firefox|Minefield|NetFront)" ]);
        }
        if (data = layout == "iCab" && parseFloat(version) > 3 && "WebKit" || /\bOpera\b/.test(name) && (/\bOPR\b/.test(ua) ? "Blink" : "Presto") || /\b(?:Midori|Nook|Safari)\b/i.test(ua) && !/^(?:Trident|EdgeHTML)$/.test(layout) && "WebKit" || !layout && /\bMSIE\b/i.test(ua) && (os == "Mac OS" ? "Tasman" : "Trident") || layout == "WebKit" && /\bPlayStation\b(?! Vita\b)/i.test(name) && "NetFront") {
            layout = [ data ];
        }
        if (name == "IE" && (data = (/; *(?:XBLWP|ZuneWP)(\d+)/i.exec(ua) || 0)[1])) {
            name += " Mobile";
            os = "Windows Phone " + (/\+$/.test(data) ? data : data + ".x");
            description.unshift("desktop mode");
        } else if (/\bWPDesktop\b/i.test(ua)) {
            name = "IE Mobile";
            os = "Windows Phone 8.x";
            description.unshift("desktop mode");
            version || (version = (/\brv:([\d.]+)/.exec(ua) || 0)[1]);
        } else if (name != "IE" && layout == "Trident" && (data = /\brv:([\d.]+)/.exec(ua))) {
            if (name) {
                description.push("identifying as " + name + (version ? " " + version : ""));
            }
            name = "IE";
            version = data[1];
        }
        if (useFeatures) {
            if (isHostType(context, "global")) {
                if (java) {
                    data = java.lang.System;
                    arch = data.getProperty("os.arch");
                    os = os || data.getProperty("os.name") + " " + data.getProperty("os.version");
                }
                if (rhino) {
                    try {
                        version = context.require("ringo/engine").version.join(".");
                        name = "RingoJS";
                    } catch (e) {
                        if ((data = context.system) && data.global.system == context.system) {
                            name = "Narwhal";
                            os || (os = data[0].os || null);
                        }
                    }
                    if (!name) {
                        name = "Rhino";
                    }
                } else if (typeof context.process == "object" && !context.process.browser && (data = context.process)) {
                    if (typeof data.versions == "object") {
                        if (typeof data.versions.electron == "string") {
                            description.push("Node " + data.versions.node);
                            name = "Electron";
                            version = data.versions.electron;
                        } else if (typeof data.versions.nw == "string") {
                            description.push("Chromium " + version, "Node " + data.versions.node);
                            name = "NW.js";
                            version = data.versions.nw;
                        }
                    }
                    if (!name) {
                        name = "Node.js";
                        arch = data.arch;
                        os = data.platform;
                        version = /[\d.]+/.exec(data.version);
                        version = version ? version[0] : null;
                    }
                }
            } else if (getClassOf(data = context.runtime) == airRuntimeClass) {
                name = "Adobe AIR";
                os = data.flash.system.Capabilities.os;
            } else if (getClassOf(data = context.phantom) == phantomClass) {
                name = "PhantomJS";
                version = (data = data.version || null) && data.major + "." + data.minor + "." + data.patch;
            } else if (typeof doc.documentMode == "number" && (data = /\bTrident\/(\d+)/i.exec(ua))) {
                version = [ version, doc.documentMode ];
                if ((data = +data[1] + 4) != version[1]) {
                    description.push("IE " + version[1] + " mode");
                    layout && (layout[1] = "");
                    version[1] = data;
                }
                version = name == "IE" ? String(version[1].toFixed(1)) : version[0];
            } else if (typeof doc.documentMode == "number" && /^(?:Chrome|Firefox)\b/.test(name)) {
                description.push("masking as " + name + " " + version);
                name = "IE";
                version = "11.0";
                layout = [ "Trident" ];
                os = "Windows";
            }
            os = os && format(os);
        }
        if (version && (data = /(?:[ab]|dp|pre|[ab]\d+pre)(?:\d+\+?)?$/i.exec(version) || /(?:alpha|beta)(?: ?\d)?/i.exec(ua + ";" + (useFeatures && nav.appMinorVersion)) || /\bMinefield\b/i.test(ua) && "a")) {
            prerelease = /b/i.test(data) ? "beta" : "alpha";
            version = version.replace(RegExp(data + "\\+?$"), "") + (prerelease == "beta" ? beta : alpha) + (/\d+\+?/.exec(data) || "");
        }
        if (name == "Fennec" || name == "Firefox" && /\b(?:Android|Firefox OS|KaiOS)\b/.test(os)) {
            name = "Firefox Mobile";
        } else if (name == "Maxthon" && version) {
            version = version.replace(/\.[\d.]+/, ".x");
        } else if (/\bXbox\b/i.test(product)) {
            if (product == "Xbox 360") {
                os = null;
            }
            if (product == "Xbox 360" && /\bIEMobile\b/.test(ua)) {
                description.unshift("mobile mode");
            }
        } else if ((/^(?:Chrome|IE|Opera)$/.test(name) || name && !product && !/Browser|Mobi/.test(name)) && (os == "Windows CE" || /Mobi/i.test(ua))) {
            name += " Mobile";
        } else if (name == "IE" && useFeatures) {
            try {
                if (context.external === null) {
                    description.unshift("platform preview");
                }
            } catch (e) {
                description.unshift("embedded");
            }
        } else if ((/\bBlackBerry\b/.test(product) || /\bBB10\b/.test(ua)) && (data = (RegExp(product.replace(/ +/g, " *") + "/([.\\d]+)", "i").exec(ua) || 0)[1] || version)) {
            data = [ data, /BB10/.test(ua) ];
            os = (data[1] ? (product = null, manufacturer = "BlackBerry") : "Device Software") + " " + data[0];
            version = null;
        } else if (this != forOwn && product != "Wii" && (useFeatures && opera || /Opera/.test(name) && /\b(?:MSIE|Firefox)\b/i.test(ua) || name == "Firefox" && /\bOS X (?:\d+\.){2,}/.test(os) || name == "IE" && (os && !/^Win/.test(os) && version > 5.5 || /\bWindows XP\b/.test(os) && version > 8 || version == 8 && !/\bTrident\b/.test(ua))) && !reOpera.test(data = parse.call(forOwn, ua.replace(reOpera, "") + ";")) && data.name) {
            data = "ing as " + data.name + ((data = data.version) ? " " + data : "");
            if (reOpera.test(name)) {
                if (/\bIE\b/.test(data) && os == "Mac OS") {
                    os = null;
                }
                data = "identify" + data;
            } else {
                data = "mask" + data;
                if (operaClass) {
                    name = format(operaClass.replace(/([a-z])([A-Z])/g, "$1 $2"));
                } else {
                    name = "Opera";
                }
                if (/\bIE\b/.test(data)) {
                    os = null;
                }
                if (!useFeatures) {
                    version = null;
                }
            }
            layout = [ "Presto" ];
            description.push(data);
        }
        if (data = (/\bAppleWebKit\/([\d.]+\+?)/i.exec(ua) || 0)[1]) {
            data = [ parseFloat(data.replace(/\.(\d)$/, ".0$1")), data ];
            if (name == "Safari" && data[1].slice(-1) == "+") {
                name = "WebKit Nightly";
                prerelease = "alpha";
                version = data[1].slice(0, -1);
            } else if (version == data[1] || version == (data[2] = (/\bSafari\/([\d.]+\+?)/i.exec(ua) || 0)[1])) {
                version = null;
            }
            data[1] = (/\b(?:Headless)?Chrome\/([\d.]+)/i.exec(ua) || 0)[1];
            if (data[0] == 537.36 && data[2] == 537.36 && parseFloat(data[1]) >= 28 && layout == "WebKit") {
                layout = [ "Blink" ];
            }
            if (!useFeatures || !likeChrome && !data[1]) {
                layout && (layout[1] = "like Safari");
                data = (data = data[0], data < 400 ? 1 : data < 500 ? 2 : data < 526 ? 3 : data < 533 ? 4 : data < 534 ? "4+" : data < 535 ? 5 : data < 537 ? 6 : data < 538 ? 7 : data < 601 ? 8 : data < 602 ? 9 : data < 604 ? 10 : data < 606 ? 11 : data < 608 ? 12 : "12");
            } else {
                layout && (layout[1] = "like Chrome");
                data = data[1] || (data = data[0], data < 530 ? 1 : data < 532 ? 2 : data < 532.05 ? 3 : data < 533 ? 4 : data < 534.03 ? 5 : data < 534.07 ? 6 : data < 534.1 ? 7 : data < 534.13 ? 8 : data < 534.16 ? 9 : data < 534.24 ? 10 : data < 534.3 ? 11 : data < 535.01 ? 12 : data < 535.02 ? "13+" : data < 535.07 ? 15 : data < 535.11 ? 16 : data < 535.19 ? 17 : data < 536.05 ? 18 : data < 536.1 ? 19 : data < 537.01 ? 20 : data < 537.11 ? "21+" : data < 537.13 ? 23 : data < 537.18 ? 24 : data < 537.24 ? 25 : data < 537.36 ? 26 : layout != "Blink" ? "27" : "28");
            }
            layout && (layout[1] += " " + (data += typeof data == "number" ? ".x" : /[.+]/.test(data) ? "" : "+"));
            if (name == "Safari" && (!version || parseInt(version) > 45)) {
                version = data;
            } else if (name == "Chrome" && /\bHeadlessChrome/i.test(ua)) {
                description.unshift("headless");
            }
        }
        if (name == "Opera" && (data = /\bzbov|zvav$/.exec(os))) {
            name += " ";
            description.unshift("desktop mode");
            if (data == "zvav") {
                name += "Mini";
                version = null;
            } else {
                name += "Mobile";
            }
            os = os.replace(RegExp(" *" + data + "$"), "");
        } else if (name == "Safari" && /\bChrome\b/.exec(layout && layout[1])) {
            description.unshift("desktop mode");
            name = "Chrome Mobile";
            version = null;
            if (/\bOS X\b/.test(os)) {
                manufacturer = "Apple";
                os = "iOS 4.3+";
            } else {
                os = null;
            }
        } else if (/\bSRWare Iron\b/.test(name) && !version) {
            version = getVersion("Chrome");
        }
        if (version && version.indexOf(data = /[\d.]+$/.exec(os)) == 0 && ua.indexOf("/" + data + "-") > -1) {
            os = trim(os.replace(data, ""));
        }
        if (os && os.indexOf(name) != -1 && !RegExp(name + " OS").test(os)) {
            os = os.replace(RegExp(" *" + qualify(name) + " *"), "");
        }
        if (layout && !/\b(?:Avant|Nook)\b/.test(name) && (/Browser|Lunascape|Maxthon/.test(name) || name != "Safari" && /^iOS/.test(os) && /\bSafari\b/.test(layout[1]) || /^(?:Adobe|Arora|Breach|Midori|Opera|Phantom|Rekonq|Rock|Samsung Internet|Sleipnir|SRWare Iron|Vivaldi|Web)/.test(name) && layout[1])) {
            (data = layout[layout.length - 1]) && description.push(data);
        }
        if (description.length) {
            description = [ "(" + description.join("; ") + ")" ];
        }
        if (manufacturer && product && product.indexOf(manufacturer) < 0) {
            description.push("on " + manufacturer);
        }
        if (product) {
            description.push((/^on /.test(description[description.length - 1]) ? "" : "on ") + product);
        }
        if (os) {
            data = / ([\d.+]+)$/.exec(os);
            isSpecialCasedOS = data && os.charAt(os.length - data[0].length - 1) == "/";
            os = {
                architecture: 32,
                family: data && !isSpecialCasedOS ? os.replace(data[0], "") : os,
                version: data ? data[1] : null,
                toString: function() {
                    const version = this.version;
                    return this.family + (version && !isSpecialCasedOS ? " " + version : "") + (this.architecture == 64 ? " 64-bit" : "");
                }
            };
        }
        if ((data = /\b(?:AMD|IA|Win|WOW|x86_|x)64\b/i.exec(arch)) && !/\bi686\b/i.test(arch)) {
            if (os) {
                os.architecture = 64;
                os.family = os.family.replace(RegExp(" *" + data), "");
            }
            if (name && (/\bWOW64\b/i.test(ua) || useFeatures && /\w(?:86|32)$/.test(nav.cpuClass || nav.platform) && !/\bWin64; x64\b/i.test(ua))) {
                description.unshift("32-bit");
            }
        } else if (os && /^OS X/.test(os.family) && name == "Chrome" && parseFloat(version) >= 39) {
            os.architecture = 64;
        }
        ua || (ua = null);
        const platform = {};
        platform.description = ua;
        platform.layout = layout && layout[0];
        platform.manufacturer = manufacturer;
        platform.name = name;
        platform.prerelease = prerelease;
        platform.product = product;
        platform.ua = ua;
        platform.version = name && version;
        platform.os = os || {
            architecture: null,
            family: null,
            version: null,
            toString: function() {
                return "null";
            }
        };
        platform.parse = parse;
        platform.toString = toStringPlatform;
        if (platform.version) {
            description.unshift(version);
        }
        if (platform.name) {
            description.unshift(name);
        }
        if (os && name && !(os == String(os).split(" ")[0] && (os == name.split(" ")[0] || product))) {
            description.push(product ? "(" + os + ")" : "on " + os);
        }
        if (description.length) {
            platform.description = description.join(" ");
        }
        return platform;
    }
    platformResult = parse();
    return platformResult;
}

function initRequestData(params) {
    if (params.dataType === "form") {
        const formData = new FormData;
        for (const key in params.data) {
            if (Object.prototype.hasOwnProperty.call(params.data, key)) {
                let dataItem = params.data[key];
                if (typeof dataItem === "object" && !(dataItem instanceof Blob)) {
                    dataItem = JSON.stringify(dataItem);
                }
                formData.append(key, dataItem);
            }
        }
        return formData;
    } else if (params.dataType === "blob") {
        return params.data;
    } else {
        return JSON.stringify(params.data);
    }
}

function initQueryParams(params) {
    const queryParams = params.queryParams || {};
    const queryParamsArray = [];
    if (params.sdkVersion) {
        queryParams["sdkVersion"] = params.sdkVersion;
    }
    for (const key in queryParams) {
        if (Object.prototype.hasOwnProperty.call(queryParams, key) && queryParams[key]) {
            queryParamsArray.push(key + "=" + queryParams[key]);
        }
    }
    if (queryParamsArray.length) {
        return "?" + queryParamsArray.join("&");
    }
    return "";
}

function initUrl(params) {
    return "https://" + (params.apiHost || "sdk-api.apptracer.ru") + params.apiUrl + initQueryParams(params);
}

function muteApiFeature(feature, ms) {
    if (featureMutedToMap[feature]) {
        return;
    }
    const lsKey = getFeatureMuteLsKey(feature);
    const lsValue = getFeatureMutedFromLs(feature);
    if (lsValue) {
        featureMutedToMap[feature] = lsValue;
    } else {
        const muteTo = featureMutedToMap[feature] = Date.now() + ms;
        getLocalStorage().setItem(lsKey, String(muteTo));
    }
}

function doBrowserRequest(params) {
    let xhr;
    const url = initUrl(params);
    const data = initRequestData(params);
    const onRequestEnd = params.onRequestEnd;
    const maxRetryInterval = 1e3 * 60 * 5;
    const headers = {};
    let retryInterval = 1e4;
    let retryCount = params.retryCount || Infinity;
    const contentTypeKey = "Content-Type";
    if (params.dataType === "json") {
        headers[contentTypeKey] = "application/json";
    } else if (params.dataType === "blob") {
        headers[contentTypeKey] = "application/octet-stream";
    }
    if (params.beforeUnload) {
        onBeforeUnload();
    } else {
        if (isWindowDefined()) {
            window.addEventListener("beforeunload", onBeforeUnload);
        }
        xhr = doRequest();
    }
    function doRequest() {
        const xhr = new XMLHttpRequest;
        xhr.open("POST", url);
        for (const key in headers) {
            if (Object.prototype.hasOwnProperty.call(headers, key) && headers[key]) {
                xhr.setRequestHeader(key, headers[key]);
            }
        }
        xhr.responseType = "json";
        xhr.onload = xhrHandler;
        xhr.onerror = xhrHandler;
        xhr.onabort = xhrHandler;
        xhr.ontimeout = xhrHandler;
        xhr.send(data);
        return xhr;
    }
    function retry() {
        retryInterval = Math.min(retryInterval * 2, maxRetryInterval);
        retryCount--;
        xhr = doRequest();
    }
    function xhrHandler(event) {
        if (!xhr) {
            return;
        }
        const isNetworkError = event.type === "timeout" || event.type === "error" && xhr.readyState === XMLHttpRequest.DONE && xhr.status === 0;
        if (isNetworkError && retryCount > 0) {
            setTimeout(retry, retryInterval);
            return;
        }
        if (typeof window !== "undefined") {
            window.removeEventListener("beforeunload", onBeforeUnload);
        }
        try {
            const result = xhr.response;
            if (result.commands) {
                const globalShutdownMs = result.commands.globalShutdownMs;
                if (globalShutdownMs) {
                    muteApiFeature("all", globalShutdownMs);
                }
                const featureShutdownMs = result.commands.featureShutdownMs;
                if (featureShutdownMs) {
                    params.onMute(featureShutdownMs);
                }
            }
        } catch (e) {}
        if (onRequestEnd) {
            onRequestEnd();
        }
    }
    function onBeforeUnload() {
        if (navigator.sendBeacon) {
            if (xhr) {
                xhr.abort();
            }
            if (typeof data === "string" || data instanceof Blob) {
                let type = headers[contentTypeKey];
                type = params.dataType === "blob" ? undefined : type;
                navigator.sendBeacon(url, new Blob([ data ], type ? {
                    type: type
                } : undefined));
            } else {
                navigator.sendBeacon(url, data);
            }
        } else if (!xhr) {
            xhr = doRequest();
        }
    }
}

function doRequest(params) {
    if (isWindowDefined()) {
        doBrowserRequest(params);
    }
}

function toBase64(value, onResult) {
    const reader = new FileReader;
    reader.onloadend = function() {
        const result = String(reader.result);
        onResult(result.slice(result.indexOf("base64,") + 7));
    };
    reader.readAsDataURL(new Blob([ value ]));
}

function getSdkVersion(sdkKey) {
    return getSdkInstance(sdkKey).version;
}

function toUpperCase(value) {
    if (!value) {
        return;
    }
    return value.toUpperCase();
}

class StackTraceUploader {
    constructor(sdkKey, options) {
        this.CRASH_BATCH_SIZE = 20;
        this.UPLOAD_BATCH_TIMEOUT_MS = 1e3 * 15;
        this.beforeUnload = false;
        this.sdkKey = sdkKey;
        this.environment = options.environment;
        this.appToken = options.appToken;
        this.versionName = options.versionName;
        this.versionCode = options.versionCode;
        this.apiHost = options.apiHost;
        this.uploadBatch = this.uploadBatch.bind(this);
        this.onUploadBatchEnd = this.onUploadBatchEnd.bind(this);
        this.onBatchDataEncoded = this.onBatchDataEncoded.bind(this);
        this.onBeforeUnload = this.onBeforeUnload.bind(this);
        if (isWindowDefined()) {
            window.addEventListener("beforeunload", this.onBeforeUnload);
        }
        this.setUploadTask(1e3);
    }
    onBeforeUnload() {
        this.beforeUnload = true;
        this.uploadBatch();
    }
    destroy() {
        clearTimeout(this.uploadBatchTimeout);
        if (isWindowDefined()) {
            window.removeEventListener("beforeunload", this.onBeforeUnload);
        }
    }
    setUploadTask(timeout = this.UPLOAD_BATCH_TIMEOUT_MS) {
        clearTimeout(this.uploadBatchTimeout);
        if (this.beforeUnload) {
            return;
        }
        this.uploadBatchTimeout = setTimeout(this.uploadBatch, timeout);
    }
    forceUpload() {
        clearTimeout(this.uploadBatchTimeout);
        this.uploadBatch();
    }
    uploadBatch() {
        const errorsData = getSdkInnerModule(this.sdkKey, 1);
        if (!errorsData || isFeatureMuted("crashUpload")) {
            this.setUploadTask();
            return;
        }
        const batchData = this.getFilteredRawBatchData(errorsData.dequeue(this.CRASH_BATCH_SIZE));
        if (!batchData.length) {
            this.setUploadTask();
            return;
        }
        this.encodeBatchDataItems(batchData, this.onBatchDataEncoded);
    }
    onBatchDataEncoded(encodedData) {
        gzip(JSON.stringify(encodedData), this.runUpload.bind(this, "GZIP"), this.runUpload.bind(this, "NONE"));
    }
    runUpload(compressType, requestData) {
        doRequest({
            beforeUnload: this.beforeUnload,
            sdkVersion: getSdkVersion(this.sdkKey),
            apiHost: this.apiHost,
            apiUrl: "/api/crash/uploadBatch",
            data: requestData,
            queryParams: {
                crashToken: this.appToken,
                compressType: compressType
            },
            dataType: "blob",
            onRequestEnd: this.onUploadBatchEnd,
            onMute: muteApiFeature.bind(null, "crashUpload")
        });
    }
    onUploadBatchEnd() {
        this.setUploadTask(0);
    }
    encodeBatchDataItems(dataItems, onReady) {
        const batchSize = dataItems.length;
        let processedItems = 0;
        for (let i = 0; i < batchSize; i++) {
            this.encodeBatchDataItem(dataItems[i], (function() {
                processedItems++;
                if (processedItems === batchSize) {
                    onReady(dataItems);
                }
            }));
        }
    }
    encodeBatchDataItem(dataItem, onReady) {
        if (dataItem.logsFile) {
            return toBase64(dataItem.logsFile, (function(value) {
                dataItem.logsFile = value;
                onReady(dataItem);
            }));
        } else {
            onReady(dataItem);
        }
    }
    getFilteredRawBatchData(errors) {
        if (!errors.length) {
            return [];
        }
        const batchData = [];
        const commonBeanInfo = this.getCommonUploadBeanInfo();
        const commonBeanProperties = this.getCommonUploadBeanProperties();
        for (let i = 0; i < errors.length; i++) {
            const errorData = errors[i];
            const nameAndMessage = errorData.error.name + ": " + errorData.error.message + "\n";
            const stackTrace = nameAndMessage + errorData.errorStack;
            const dataItem = {
                type: errorData.type,
                format: "JS_STACKTRACE",
                severity: toUpperCase(errorData.severity) || errorData.type,
                uploadBean: this.getUploadBeanForError(errorData, commonBeanInfo, commonBeanProperties),
                stackTrace: stackTrace
            };
            if (errorData.log) {
                dataItem.logsFile = errorData.log;
            }
            batchData.push(dataItem);
        }
        return batchData;
    }
    getCommonUploadBeanProperties() {
        var _a, _b, _c;
        let commonProperties = this.getWindowParams();
        {
            const parserResults = getUserAgentData();
            commonProperties = Object.assign(Object.assign({}, commonProperties), {
                userAgent: parserResults.ua,
                browserName: parserResults.name,
                browserVersion: parserResults.version,
                osFamily: (_a = parserResults.os) === null || _a === void 0 ? void 0 : _a.family,
                osVersion: (_b = parserResults.os) === null || _b === void 0 ? void 0 : _b.version,
                osArchitecture: (_c = parserResults.os) === null || _c === void 0 ? void 0 : _c.architecture
            });
        }
        return commonProperties;
    }
    getCommonUploadBeanInfo() {
        var _a;
        {
            const unknown = "unknown";
            const parserResults = getUserAgentData();
            return {
                osVersion: ((_a = parserResults.os) === null || _a === void 0 ? void 0 : _a.version) || unknown,
                vendor: parserResults.name || unknown,
                device: parserResults.version || unknown
            };
        }
    }
    getUploadBeanForError(errorData, commonBeanInfo, commonBeanProperties) {
        const tags = [];
        const keys = errorData.keys;
        if (keys) {
            for (const key in keys) {
                if (key && Object.prototype.hasOwnProperty.call(keys, key)) {
                    tags.push(`${key}=${keys[key]}`);
                }
            }
        }
        if (errorData.crashIdSource) {
            commonBeanInfo.crashIdSourceField = "crashIdSource";
            commonBeanProperties.crashIdSource = errorData.crashIdSource;
        }
        const sdkKey = this.sdkKey;
        const properties = Object.assign({
            tracerSdkVersion: getSdkVersion(sdkKey),
            timestamp: errorData.timestamp,
            date: rfc3339(new Date(errorData.timestamp)),
            userId: errorData.userId ? String(errorData.userId) : undefined,
            issueKey: errorData.issueKey,
            errorEventType: errorData.errorEventType,
            documentVisibilityState: errorData.documentVisibilityState,
            errorCause: errorData.causeSimpleValue
        }, commonBeanProperties);
        return Object.assign({
            crashIdSource: errorData.crashIdSource,
            environment: this.environment,
            component: errorData.component || (sdkKey !== mainSdkKey ? sdkKey : undefined),
            deviceId: getDeviceId(),
            sessionUuid: getSessionUuid(),
            versionName: errorData.versionName || this.versionName,
            versionCode: errorData.versionCode || this.versionCode,
            tags: tags.length ? tags : undefined,
            module: errorData.module,
            properties: properties
        }, commonBeanInfo);
    }
    getWindowParams() {
        if (isWindowDefined()) {
            const screen = window.screen;
            const data = {
                screenWidth: screen.width,
                screenHeight: screen.height,
                host: window.location.host
            };
            if (screen.orientation && screen.orientation.angle !== undefined) {
                data.screenOrientationAngle = screen.orientation.angle;
            }
            return data;
        }
        return {};
    }
}

function innerInitTracerErrorUploader(sdkKey, config) {
    initSdkInnerModule(sdkKey, 3, (function() {
        return new StackTraceUploader(sdkKey, {
            appToken: config.appToken,
            apiHost: config.apiHost,
            versionName: config.versionName,
            versionCode: config.versionCode,
            environment: config.environment
        });
    }));
}

function initTracerErrorUploader(config) {
    return innerInitTracerErrorUploader(mainSdkKey, config);
}

let AsyncFunction;

function getAsyncFunction() {
    try {
        if (!AsyncFunction) {
            AsyncFunction = new Function("return (async function() { }).constructor;")();
        }
        return AsyncFunction;
    } catch (e) {
        return null;
    }
}

function isAsyncFunction(fn) {
    const AsyncFunction = getAsyncFunction();
    return typeof fn === "function" && AsyncFunction !== null && fn instanceof AsyncFunction;
}

function initScope(name, stack, scope) {
    if (scope && scope.stack === stack) {
        scope.sameScopeCount++;
        startScope(scope);
    } else {
        startScope({
            name: name,
            stack: stack,
            parent: scope,
            sameScopeCount: 1
        });
    }
}

function catchEndScope(e) {
    if (isError(e) && !e.tracerScope) {
        e.tracerScope = getCurrentScope();
    }
    endScope();
}

function getCurrentStack() {
    return new Error(tracerCurrentStackKey).stack || "";
}

let disableCheckNativeFunctions = false;

function getDisableCheckNativeFunctions() {
    return disableCheckNativeFunctions;
}

function setDisableCheckNativeFunctions(value) {
    disableCheckNativeFunctions = value;
}

function wrapHandler(name, handler) {
    if (!handler || typeof handler !== "function" || handler.tracerWrapper) {
        return handler;
    }
    if (!getDisableCheckNativeFunctions()) {
        if (handler.tracerIsNativeFunction === undefined) {
            handler.tracerIsNativeFunction = handler.toString().indexOf("[native code]") !== -1;
        }
        if (handler.tracerIsNativeFunction) {
            return handler;
        }
    }
    const scope = getCurrentScope();
    const stack = getCurrentStack();
    let wrappedFn;
    if (isAsyncFunction(handler)) {
        wrappedFn = getTracerHandlerWrapAsync()(name, handler, stack, scope, initScope, endScope, catchEndScope);
    } else {
        wrappedFn = function tracerHandlerWrap(...args) {
            initScope(name, stack, scope);
            try {
                const result = handler(...args);
                endScope();
                return result;
            } catch (e) {
                catchEndScope(e);
                throw e;
            }
        };
    }
    wrappedFn.tracerWrapper = true;
    return wrappedFn;
}

let tracerHandlerWrapAsyncFn;

function getTracerHandlerWrapAsync() {
    if (tracerHandlerWrapAsyncFn) {
        return tracerHandlerWrapAsyncFn;
    }
    tracerHandlerWrapAsyncFn = new Function("name", "handler", "stack", "scope", "initScope", "endScope", "catchEndScope", `return async function tracerHandlerWrap(...args) {\n            initScope(name, stack, scope);\n\n            try {\n                const result = await handler(...args);\n                endScope();\n                return result;\n            } catch (e) {\n                catchEndScope(e);\n                throw e;\n            }\n        };`);
    return tracerHandlerWrapAsyncFn;
}

class TracerPromise extends Promise {
    constructor(executor) {
        super(wrapHandler("Promise.constructor", executor));
        Object.setPrototypeOf(this, TracerPromise.prototype);
    }
}

if (getAsyncFunction()) {
    const OriginalPromise = Promise;
    Object.defineProperty(TracerPromise, Symbol.hasInstance, {
        value: function(item) {
            return item instanceof OriginalPromise;
        }
    });
}

function getBrowserGlobal() {
    if (isWindowDefined()) {
        return window;
    }
    if (typeof self !== "undefined" && typeof self === "object" && self.self === self) {
        return self;
    }
    return null;
}

function patchPromise() {
    const browserGlobal = getBrowserGlobal();
    if (!browserGlobal) {
        return {
            destroy: function() {}
        };
    }
    const OriginalPromise = browserGlobal.Promise;
    const originalThen = OriginalPromise.prototype.then;
    const originalCatch = OriginalPromise.prototype.catch;
    const originalFinally = OriginalPromise.prototype.finally;
    OriginalPromise.prototype.then = function(onfulfilled, onrejected) {
        if (this.tracerNoWrap) {
            return originalThen.call(this, onfulfilled, onrejected);
        }
        onrejected = onrejected || function(e) {
            throw e;
        };
        return originalThen.call(this, wrapHandler("Promise.then onFulfilled", onfulfilled), wrapHandler("Promise.then onRejected", onrejected));
    };
    OriginalPromise.prototype.catch = function(onrejected) {
        this.tracerNoWrap = true;
        const promise = originalCatch.call(this, wrapHandler("Promise.catch", onrejected));
        this.tracerNoWrap = false;
        return promise;
    };
    OriginalPromise.prototype.finally = function(onfinally) {
        this.tracerNoWrap = true;
        const promise = originalFinally.call(this, wrapHandler("Promise.finally", onfinally));
        this.tracerNoWrap = false;
        return promise;
    };
    browserGlobal.Promise = TracerPromise;
    return {
        destroy: function() {
            browserGlobal.Promise = OriginalPromise;
            OriginalPromise.prototype.then = originalThen;
            OriginalPromise.prototype.catch = originalCatch;
            OriginalPromise.prototype.finally = originalFinally;
        }
    };
}

function patchSetTimeout() {
    const browserGlobal = getBrowserGlobal();
    if (!browserGlobal) {
        return {
            destroy: function() {}
        };
    }
    const setTimeoutOriginal = browserGlobal.setTimeout;
    browserGlobal.setTimeout = function tracerSetTimeout(handler, timeout, ...args) {
        return setTimeoutOriginal(wrapHandler("setTimeout", handler), timeout, ...args);
    };
    return {
        destroy: function() {
            browserGlobal.setTimeout = setTimeoutOriginal;
        }
    };
}

function patchSetInterval() {
    const browserGlobal = getBrowserGlobal();
    if (!browserGlobal) {
        return {
            destroy: function() {}
        };
    }
    const setIntervalOriginal = browserGlobal.setInterval;
    browserGlobal.setInterval = function tracerSetInterval(handler, timeout, ...args) {
        return setIntervalOriginal(wrapHandler("setInterval", handler), timeout, ...args);
    };
    return {
        destroy: function() {
            browserGlobal.setInterval = setIntervalOriginal;
        }
    };
}

function destroySdkInnerModule(sdkKey, dataItemKey) {
    const sdkData = getSdkInnerModules(sdkKey);
    const dataItem = sdkData[dataItemKey];
    if (dataItem) {
        dataItem.destroy();
        sdkData[dataItemKey] = undefined;
    }
}

function innerInitTracerErrorAsyncStack(sdkKey, config = {}) {
    initSdkInnerModule(sdkKey, 4, (function() {
        setDisableCheckNativeFunctions(Boolean(config.disableCheckNativeFunctions));
        let patches = [ patchPromise(), patchSetTimeout(), patchSetInterval() ];
        return {
            disableStackCleanup: Boolean(config.disableStackCleanup),
            destroy: function() {
                for (let i = 0, ln = patches.length; i < ln; i++) {
                    patches[i].destroy();
                }
                patches = [];
                clearScope();
            }
        };
    }));
    return initSdkPublicModule(sdkKey, "async", (function() {
        return {
            disableAsyncStack() {
                destroySdkInnerModule(sdkKey, 4);
            }
        };
    }));
}

function initTracerErrorAsyncStack(config = {}) {
    return innerInitTracerErrorAsyncStack(mainSdkKey, config);
}

const PerformanceUnitTime = {
    NANOSECOND: "ns",
    MICROSECOND: "us",
    MILLISECOND: "ms",
    SECOND: "s",
    MINUTE: "min",
    HOUR: "h",
    DAY: "d"
};

const PerformanceUnitData = {
    BYTES: "By",
    KILOBYTES: "KBy",
    MEGABYTES: "MBy",
    GIGABYTES: "GBy",
    TERABYTES: "TBy",
    PETABYTES: "PBy"
};

const PerformanceUnitValue = {
    INT: "1",
    FLOAT: "10*-n",
    PERCENT: "%"
};

const withPerformance = typeof performance !== "undefined";

function getNow() {
    return withPerformance ? performance.now() : Date.now();
}

function msToNano(ms) {
    return Math.round(ms * 1e6);
}

function getTimestamp() {
    let time;
    if (!withPerformance || !performance.now) {
        time = Date.now();
    } else if (performance.timeOrigin) {
        time = performance.timeOrigin + performance.now();
    } else if (performance.timing && performance.timing.navigationStart) {
        time = performance.timing.navigationStart + performance.now();
    } else {
        time = Date.now() + performance.now();
    }
    return time;
}

function getTimeLabelKey(name) {
    return typeof name === "string" ? name : name.join("-");
}

function getTimeLabelName(name) {
    return typeof name === "string" ? name : name[0];
}

class PerformanceData {
    constructor(sdkKey) {
        this.samples = [];
        this.timeLabel = {};
        this.sdkKey = sdkKey;
    }
    destroy() {
        this.samples = [];
        this.timeLabel = {};
    }
    time(name, attributes) {
        const key = getTimeLabelKey(name);
        if (this.timeLabel[key]) {
            console.warn(`Timer '${getTimeLabelName(name)}' already exists`);
            return;
        }
        this.timeLabel[key] = {
            from: getNow(),
            attributes: Object.assign({}, attributes)
        };
    }
    timeEnd(name, attributes) {
        const key = getTimeLabelKey(name);
        const timeLabelItem = this.timeLabel[key];
        if (timeLabelItem === null) {
            return;
        }
        if (!timeLabelItem) {
            console.warn(`Timer '${getTimeLabelName(name)}' does not exist`);
            return;
        }
        this.timeLabel[key] = undefined;
        this.add({
            name: getTimeLabelName(name),
            value: msToNano(getNow() - timeLabelItem.from),
            unit: PerformanceUnitTime.NANOSECOND,
            attributes: Object.assign(Object.assign({}, timeLabelItem.attributes), attributes)
        });
    }
    timeAbort(name) {
        this.timeLabel[getTimeLabelKey(name)] = null;
    }
    add(item) {
        if (isFeatureMuted("perfUpload")) {
            return;
        }
        if (item.value < 0) {
            throw new Error("Tracer performance do not support negative values");
        }
        let sample;
        const timeUnixNano = msToNano(getTimestamp());
        if (item.unit === PerformanceUnitValue.FLOAT) {
            sample = {
                name: item.name,
                timeUnixNano: timeUnixNano,
                value: Math.round(item.value * Math.pow(10, item.precision)),
                unit: "10*-" + item.precision,
                attributes: item.attributes
            };
        } else {
            sample = {
                name: item.name,
                timeUnixNano: timeUnixNano,
                value: item.value,
                unit: item.unit,
                attributes: item.attributes
            };
        }
        this.samples.push(sample);
    }
    getData() {
        return this.samples;
    }
    clear() {
        this.samples = [];
    }
    dequeue(count = 1) {
        return this.samples.splice(0, count);
    }
}

function sampleEnd(sdkKey, name, from, startAttributes, endAttributes) {
    const data = getSdkInnerModule(sdkKey, 5);
    if (data) {
        data.add({
            name: name,
            value: msToNano(getNow() - from),
            unit: PerformanceUnitTime.NANOSECOND,
            attributes: Object.assign(Object.assign({}, startAttributes), endAttributes)
        });
    }
}

function emptyFunction() {}

class TracerPerformance {
    constructor(sdkKey) {
        this.sdkKey = sdkKey;
    }
    getPerformanceData() {
        return getSdkInnerModule(this.sdkKey, 5);
    }
    addSample(sample) {
        const perfData = this.getPerformanceData();
        if (perfData) {
            perfData.add(sample);
        }
    }
    addSampleTime(name, value, attributes, unit = PerformanceUnitTime.MILLISECOND) {
        const perfData = this.getPerformanceData();
        if (perfData) {
            perfData.add({
                name: name,
                value: value,
                attributes: attributes,
                unit: unit
            });
        }
    }
    startTimer(name, attributes) {
        const sdkKey = this.sdkKey;
        const from = getNow();
        const timer = {
            endTimer: function() {
                sampleEnd(sdkKey, name, from, attributes);
                timer.endTimer = emptyFunction;
            },
            abortTimer: function abort() {
                timer.endTimer = emptyFunction;
                timer.abortTimer = emptyFunction;
            }
        };
        return timer;
    }
    time(name, attributes) {
        const perfData = this.getPerformanceData();
        if (perfData) {
            perfData.time(name, attributes);
        }
    }
    timeEnd(name, attributes) {
        const perfData = this.getPerformanceData();
        if (perfData) {
            perfData.timeEnd(name, attributes);
        }
    }
    timeAbort(name) {
        const perfData = this.getPerformanceData();
        if (perfData) {
            perfData.timeAbort(name);
        }
    }
}

function innerInitTracerPerformance(sdkKey) {
    initSdkInnerModule(sdkKey, 5, (function() {
        return new PerformanceData(sdkKey);
    }));
    return initSdkPublicModule(sdkKey, "performance", (function() {
        return new TracerPerformance(sdkKey);
    }));
}

function initTracerPerformance() {
    return innerInitTracerPerformance(mainSdkKey);
}

class PerformanceUploader {
    constructor(sdkKey, options) {
        this.PERFORMANCE_SAMPLE_DATA_SIZE = 500;
        this.PERFORMANCE_UPLOAD_TIMEOUT = 1e3 * 15;
        this.beforeUnload = false;
        this.sdkKey = sdkKey;
        this.appToken = options.appToken;
        this.versionName = options.versionName;
        this.versionCode = options.versionCode;
        this.apiHost = options.apiHost;
        this.upload = this.upload.bind(this);
        this.onBeforeUnload = this.onBeforeUnload.bind(this);
        if (isWindowDefined()) {
            window.addEventListener("beforeunload", this.onBeforeUnload);
        }
        this.setUploadTask(1e3);
    }
    onBeforeUnload() {
        this.beforeUnload = true;
        this.upload();
    }
    destroy() {
        clearInterval(this.uploadTimeout);
        if (isWindowDefined()) {
            window.removeEventListener("beforeunload", this.onBeforeUnload);
        }
    }
    setUploadTask(timeout = this.PERFORMANCE_UPLOAD_TIMEOUT) {
        clearTimeout(this.uploadTimeout);
        if (this.beforeUnload) {
            return;
        }
        this.uploadTimeout = setTimeout(this.upload, timeout);
    }
    upload() {
        var _a;
        const samplesData = getSdkInnerModule(this.sdkKey, 5);
        if (!samplesData || isFeatureMuted("perfUpload")) {
            this.setUploadTask();
            return;
        }
        const samples = samplesData.dequeue(this.PERFORMANCE_SAMPLE_DATA_SIZE);
        if (!samples.length) {
            this.setUploadTask();
            return;
        }
        const requestData = {};
        requestData.samples = samples;
        requestData.versionName = this.versionName;
        requestData.versionCode = String(this.versionCode);
        requestData.sessionUuid = getSessionUuid();
        requestData.deviceId = getDeviceId();
        requestData.clientTimeUnixNano = String(msToNano(getTimestamp()));
        {
            const unknown = "unknown";
            const parserResults = getUserAgentData();
            requestData.osVersion = ((_a = parserResults.os) === null || _a === void 0 ? void 0 : _a.version) || unknown;
            requestData.vendor = parserResults.name || unknown;
            requestData.device = parserResults.version || unknown;
        }
        const onRequestEnd = this.onUploadEnd.bind(this);
        doRequest({
            beforeUnload: this.beforeUnload,
            sdkVersion: getSdkVersion(this.sdkKey),
            apiHost: this.apiHost,
            apiUrl: "/api/perf/upload",
            queryParams: {
                crashToken: this.appToken
            },
            data: requestData,
            dataType: "json",
            onRequestEnd: onRequestEnd,
            onMute: muteApiFeature.bind(null, "perfUpload")
        });
    }
    onUploadEnd() {
        this.setUploadTask(0);
    }
}

function innerInitTracerPerformanceUploader(sdkKey, config) {
    initSdkInnerModule(sdkKey, 7, (function() {
        return new PerformanceUploader(sdkKey, config);
    }));
}

function initTracerPerformanceUploader(config) {
    return innerInitTracerPerformanceUploader(mainSdkKey, config);
}

var e, o = -1, a = function a(e) {
    addEventListener("pageshow", (function(n) {
        n.persisted && (o = n.timeStamp, e(n));
    }), !0);
}, c = function c() {
    var e = self.performance && performance.getEntriesByType && performance.getEntriesByType("navigation")[0];
    if (e && e.responseStart > 0 && e.responseStart < performance.now()) return e;
}, u = function u() {
    var e = c();
    return e && e.activationStart || 0;
}, f = function f(e, n) {
    var t = c(), r = "navigate";
    o >= 0 ? r = "back-forward-cache" : t && (document.prerendering || u() > 0 ? r = "prerender" : document.wasDiscarded ? r = "restore" : t.type && (r = t.type.replace(/_/g, "-")));
    return {
        name: e,
        value: void 0 === n ? -1 : n,
        rating: "good",
        delta: 0,
        entries: [],
        id: "v4-".concat(Date.now(), "-").concat(Math.floor(8999999999999 * Math.random()) + 1e12),
        navigationType: r
    };
}, s = function s(e, n, t) {
    try {
        if (PerformanceObserver.supportedEntryTypes.includes(e)) {
            var r = new PerformanceObserver((function(e) {
                Promise.resolve().then((function() {
                    n(e.getEntries());
                }));
            }));
            return r.observe(Object.assign({
                type: e,
                buffered: !0
            }, t || {})), r;
        }
    } catch (e) {}
}, d = function d(e, n, t, r) {
    var i, o;
    return function(a) {
        n.value >= 0 && (a || r) && ((o = n.value - (i || 0)) || void 0 === i) && (i = n.value, 
        n.delta = o, n.rating = function(e, n) {
            return e > n[1] ? "poor" : e > n[0] ? "needs-improvement" : "good";
        }(n.value, t), e(n));
    };
}, l = function l(e) {
    requestAnimationFrame((function() {
        return requestAnimationFrame((function() {
            return e();
        }));
    }));
}, p = function p(e) {
    document.addEventListener("visibilitychange", (function() {
        "hidden" === document.visibilityState && e();
    }));
}, v = function v(e) {
    var n = !1;
    return function() {
        n || (e(), n = !0);
    };
}, m = -1, h = function h() {
    return "hidden" !== document.visibilityState || document.prerendering ? 1 / 0 : 0;
}, g = function g(e) {
    "hidden" === document.visibilityState && m > -1 && (m = "visibilitychange" === e.type ? e.timeStamp : 0, 
    T());
}, y = function y() {
    addEventListener("visibilitychange", g, !0), addEventListener("prerenderingchange", g, !0);
}, T = function T() {
    removeEventListener("visibilitychange", g, !0), removeEventListener("prerenderingchange", g, !0);
}, E = function E() {
    return m < 0 && (m = h(), y(), a((function() {
        setTimeout((function() {
            m = h(), y();
        }), 0);
    }))), {
        get firstHiddenTime() {
            return m;
        }
    };
}, C = function C(e) {
    document.prerendering ? addEventListener("prerenderingchange", (function() {
        return e();
    }), !0) : e();
}, b = [ 1800, 3e3 ], S = function S(e, n) {
    n = n || {}, C((function() {
        var t, r = E(), i = f("FCP"), o = s("paint", (function(e) {
            e.forEach((function(e) {
                "first-contentful-paint" === e.name && (o.disconnect(), e.startTime < r.firstHiddenTime && (i.value = Math.max(e.startTime - u(), 0), 
                i.entries.push(e), t(!0)));
            }));
        }));
        o && (t = d(e, i, b, n.reportAllChanges), a((function(r) {
            i = f("FCP"), t = d(e, i, b, n.reportAllChanges), l((function() {
                i.value = performance.now() - r.timeStamp, t(!0);
            }));
        })));
    }));
}, L = [ .1, .25 ], w = function w(e, n) {
    n = n || {}, S(v((function() {
        var t, r = f("CLS", 0), i = 0, o = [], c = function c(e) {
            e.forEach((function(e) {
                if (!e.hadRecentInput) {
                    var n = o[0], t = o[o.length - 1];
                    i && e.startTime - t.startTime < 1e3 && e.startTime - n.startTime < 5e3 ? (i += e.value, 
                    o.push(e)) : (i = e.value, o = [ e ]);
                }
            })), i > r.value && (r.value = i, r.entries = o, t());
        }, u = s("layout-shift", c);
        u && (t = d(e, r, L, n.reportAllChanges), p((function() {
            c(u.takeRecords()), t(!0);
        })), a((function() {
            i = 0, r = f("CLS", 0), t = d(e, r, L, n.reportAllChanges), l((function() {
                return t();
            }));
        })), setTimeout(t, 0));
    })));
}, A = 0, I = 1 / 0, P = 0, M = function M(e) {
    e.forEach((function(e) {
        e.interactionId && (I = Math.min(I, e.interactionId), P = Math.max(P, e.interactionId), 
        A = P ? (P - I) / 7 + 1 : 0);
    }));
}, k = function k() {
    return e ? A : performance.interactionCount || 0;
}, F = function F() {
    "interactionCount" in performance || e || (e = s("event", M, {
        type: "event",
        buffered: !0,
        durationThreshold: 0
    }));
}, D = [], x = new Map, R = 0, B = function B() {
    var e = Math.min(D.length - 1, Math.floor((k() - R) / 50));
    return D[e];
}, H = [], q = function q(e) {
    if (H.forEach((function(n) {
        return n(e);
    })), e.interactionId || "first-input" === e.entryType) {
        var n = D[D.length - 1], t = x.get(e.interactionId);
        if (t || D.length < 10 || e.duration > n.latency) {
            if (t) e.duration > t.latency ? (t.entries = [ e ], t.latency = e.duration) : e.duration === t.latency && e.startTime === t.entries[0].startTime && t.entries.push(e); else {
                var r = {
                    id: e.interactionId,
                    latency: e.duration,
                    entries: [ e ]
                };
                x.set(r.id, r), D.push(r);
            }
            D.sort((function(e, n) {
                return n.latency - e.latency;
            })), D.length > 10 && D.splice(10).forEach((function(e) {
                return x["delete"](e.id);
            }));
        }
    }
}, O = function O(e) {
    var n = self.requestIdleCallback || self.setTimeout, t = -1;
    return e = v(e), "hidden" === document.visibilityState ? e() : (t = n(e), p(e)), 
    t;
}, N = [ 200, 500 ], j = function j(e, n) {
    "PerformanceEventTiming" in self && "interactionId" in PerformanceEventTiming.prototype && (n = n || {}, 
    C((function() {
        var t;
        F();
        var r, i = f("INP"), o = function o(e) {
            O((function() {
                e.forEach(q);
                var n = B();
                n && n.latency !== i.value && (i.value = n.latency, i.entries = n.entries, r());
            }));
        }, c = s("event", o, {
            durationThreshold: null !== (t = n.durationThreshold) && void 0 !== t ? t : 40
        });
        r = d(e, i, N, n.reportAllChanges), c && (c.observe({
            type: "first-input",
            buffered: !0
        }), p((function() {
            o(c.takeRecords()), r(!0);
        })), a((function() {
            R = k(), D.length = 0, x.clear(), i = f("INP"), r = d(e, i, N, n.reportAllChanges);
        })));
    })));
}, _ = [ 2500, 4e3 ], z = {}, G = function G(e, n) {
    n = n || {}, C((function() {
        var t, r = E(), i = f("LCP"), o = function o(e) {
            n.reportAllChanges || (e = e.slice(-1)), e.forEach((function(e) {
                e.startTime < r.firstHiddenTime && (i.value = Math.max(e.startTime - u(), 0), i.entries = [ e ], 
                t());
            }));
        }, c = s("largest-contentful-paint", o);
        if (c) {
            t = d(e, i, _, n.reportAllChanges);
            var m = v((function() {
                z[i.id] || (o(c.takeRecords()), c.disconnect(), z[i.id] = !0, t(!0));
            }));
            [ "keydown", "click" ].forEach((function(e) {
                addEventListener(e, (function() {
                    return O(m);
                }), {
                    once: !0,
                    capture: !0
                });
            })), p(m), a((function(r) {
                i = f("LCP"), t = d(e, i, _, n.reportAllChanges), l((function() {
                    i.value = performance.now() - r.timeStamp, z[i.id] = !0, t(!0);
                }));
            }));
        }
    }));
}, J = [ 800, 1800 ], K = function e(n) {
    document.prerendering ? C((function() {
        return e(n);
    })) : "complete" !== document.readyState ? addEventListener("load", (function() {
        return e(n);
    }), !0) : setTimeout(n, 0);
}, Q = function Q(e, n) {
    n = n || {};
    var t = f("TTFB"), r = d(e, t, J, n.reportAllChanges);
    K((function() {
        var i = c();
        i && (t.value = Math.max(i.responseStart - u(), 0), t.entries = [ i ], r(!0), a((function() {
            t = f("TTFB", 0), (r = d(e, t, J, n.reportAllChanges))(!0);
        })));
    }));
};

function getNodeTagAndClassName(node) {
    const nodeName = node.nodeName.toLowerCase();
    const classNames = getClassNames(node);
    if (!classNames || !classNames.length) {
        const parent = node.parentNode;
        if (parent) {
            return getNodeTagAndClassName(parent) + " > " + nodeName;
        }
    }
    return [ nodeName ].concat(getClassNames(node) || []).join(".");
}

function getClassNames(node) {
    let className;
    if (node instanceof Element) {
        if (node instanceof SVGElement) {
            className = node.getAttribute("class");
        } else {
            className = node.className;
        }
    }
    return className ? className.split(" ") : undefined;
}

class TracerWebVitalsData {
    constructor(sdkKey, config) {
        this.disabled = false;
        this.sdkKey = sdkKey;
        if (TracerWebVitalsData.instance) {
            TracerWebVitalsData.instance.disabled = false;
            return TracerWebVitalsData.instance;
        }
        this.addMetricAttributes = config.addMetricAttributes;
        const options = {
            reportAllChanges: true
        };
        w(this.onMetric.bind(this, {}), options);
        S(this.onMetric.bind(this, {}), options);
        G(this.onMetric.bind(this, {
            subname: "delta"
        }), options);
        G(this.onMetric.bind(this, {
            value: true
        }));
        j(this.onMetric.bind(this, {}), options);
        Q(this.onMetric.bind(this, {}), options);
        TracerWebVitalsData.instance = this;
    }
    getPerformanceData() {
        return getSdkInnerModule(this.sdkKey, 5);
    }
    collectNodePaths(input, outputKey) {
        if (!input) {
            return {};
        }
        const nodesPaths = [];
        input.map((function(item) {
            let node;
            if ("node" in item) {
                node = item.node;
            } else if ("element" in item) {
                node = item.element;
            } else if ("target" in item) {
                node = item.target;
            }
            if (node) {
                nodesPaths.push(getNodeTagAndClassName(node));
            }
        }));
        if (!nodesPaths.length) {
            return {};
        }
        return {
            [outputKey]: nodesPaths.join(", ")
        };
    }
    getAttributesCLS(data) {
        const entry = data.entries[data.entries.length - 1];
        return entry ? this.collectNodePaths(entry.sources, "entrySourceNodes") : {};
    }
    getAttributesLCP(data) {
        return this.collectNodePaths(data.entries, "entryElements");
    }
    getAttributesINP(data) {
        return this.collectNodePaths(data.entries, "entryTargets");
    }
    onMetric(options, data) {
        if (this.disabled) {
            return;
        }
        const dataValueKey = options.value ? "value" : "delta";
        if (data[dataValueKey] <= 0) {
            return;
        }
        const perfData = this.getPerformanceData();
        if (!perfData) {
            return;
        }
        let attributes;
        if (this.addMetricAttributes) {
            attributes = this.addMetricAttributes(data);
        } else {
            attributes = {};
        }
        let name = "Web Vitals - " + data.name;
        if (options.subname) {
            name += " (" + options.subname + ")";
        }
        let specificAttributes = {};
        switch (data.name) {
          case "CLS":
            specificAttributes = this.getAttributesCLS(data);
            break;

          case "LCP":
            specificAttributes = this.getAttributesLCP(data);
            break;

          case "INP":
            specificAttributes = this.getAttributesINP(data);
            break;
        }
        attributes = Object.assign(Object.assign({}, attributes), specificAttributes);
        attributes["rating"] = data.rating;
        attributes["navigationType"] = data.navigationType;
        switch (data.name) {
          case "CLS":
            perfData.add({
                name: name,
                unit: PerformanceUnitValue.FLOAT,
                precision: 6,
                value: data[dataValueKey],
                attributes: attributes
            });
            break;

          default:
            perfData.add({
                name: name,
                unit: PerformanceUnitTime.NANOSECOND,
                value: msToNano(data[dataValueKey]),
                attributes: attributes
            });
        }
    }
    destroy() {
        this.disabled = true;
    }
}

function innerInitTracerWebVitals(sdkKey, config = {}) {
    initSdkInnerModule(sdkKey, 5, (function() {
        return new PerformanceData(sdkKey);
    }));
    initSdkInnerModule(sdkKey, 9, (function() {
        return new TracerWebVitalsData(sdkKey, config);
    }));
}

function initTracerWebVitals(config = {}) {
    return innerInitTracerWebVitals(mainSdkKey, config);
}

function innerInitTracerWebVitalsUploader(sdkKey, config) {
    innerInitTracerPerformanceUploader(sdkKey, config);
}

function initTracerWebVitalsUploader(config) {
    return innerInitTracerWebVitalsUploader(mainSdkKey, config);
}

function flattenObject(obj, parentKey = "", result = {}) {
    for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
            const currentKey = parentKey ? parentKey + "." + key : key;
            const value = obj[key];
            if (value !== null && value !== undefined) {
                if (Array.isArray(value)) {
                    result[currentKey] = value.join(", ");
                } else if (isObject(value)) {
                    flattenObject(value, currentKey, result);
                } else {
                    result[currentKey] = String(value);
                }
            }
        }
    }
    return result;
}

const startTimestamp = Date.now();

function getTimeFromStart() {
    if (!withPerformance || !performance.now) {
        return Date.now() - startTimestamp;
    } else {
        return performance.now();
    }
}

const oneMin = 1e3 * 60;

const oneHr = oneMin * 60;

const defaultMemoryUsageTimePoints = [ {
    name: "initial",
    time: 0
}, {
    name: "5 min",
    time: oneMin * 5
}, {
    name: "15 min",
    time: oneMin * 15
}, {
    name: "60 min",
    time: oneHr
}, {
    name: "3 hr",
    time: oneHr * 3
}, {
    name: "12 hr",
    time: oneHr * 12
} ];

const metricNameBase = "Memory - ";

class PerformanceMemoryData {
    constructor(sdkKey, config) {
        this.measurePoints = [];
        this.sdkKey = sdkKey;
        this.config = config;
        const memoryUsageTimePoints = config.memoryUsageTimePoints || defaultMemoryUsageTimePoints;
        for (let i = 0; i < memoryUsageTimePoints.length; i++) {
            const point = memoryUsageTimePoints[i];
            this.measurePoints.push({
                from: point.time,
                to: point.time + oneMin,
                name: point.name
            });
        }
        this.runMeasureTask = this.runMeasureTask.bind(this);
        this.runMeasureTask();
    }
    setMeasureTask(timeout = 1e4) {
        clearTimeout(this.measureTimeout);
        this.measureTimeout = setTimeout(this.runMeasureTask, timeout);
    }
    runMeasureTask() {
        if (!this.measurePoints.length) {
            return;
        }
        const pointName = this.getMeasurePointName();
        if (pointName) {
            this.measureMemory(pointName);
        }
        this.setMeasureTask();
    }
    getMeasurePointName() {
        const timeFromStart = getTimeFromStart();
        for (let i = this.measurePoints.length - 1; i >= 0; i--) {
            const point = this.measurePoints[i];
            if (timeFromStart > point.to) {
                this.measurePoints.splice(i, 1);
                continue;
            }
            if (timeFromStart >= point.from && timeFromStart <= point.to) {
                this.measurePoints.splice(i, 1);
                return point.name;
            }
        }
    }
    measureMemory(name, attributes) {
        try {
            this.doMeasureUnsafe(name, attributes);
        } catch (e) {}
    }
    doMeasureUnsafe(name, customAttributes) {
        if (!isWindowDefined()) {
            return;
        }
        const performance = window.performance;
        if (!performance) {
            return;
        }
        const perfData = getSdkInnerModule(this.sdkKey, 5);
        if (!perfData) {
            return;
        }
        const attributesProvider = this.config.memoryUsageAttributes;
        const attributesFromProvider = attributesProvider ? attributesProvider(name) : {};
        if (this.config.enableMeasureUserAgentSpecificMemory && window.crossOriginIsolated && performance.measureUserAgentSpecificMemory) {
            performance.measureUserAgentSpecificMemory().then((function(data) {
                const metricName = metricNameBase + "userAgentSpecificMemory";
                perfData.add({
                    name: metricName,
                    value: data.bytes,
                    unit: PerformanceUnitData.BYTES,
                    attributes: Object.assign(Object.assign(Object.assign({}, attributesFromProvider), customAttributes), {
                        pointName: name
                    })
                });
                const breakdown = data.breakdown;
                if (breakdown) {
                    for (let i = 0, l = breakdown.length; i < l; i++) {
                        const breakdownItem = breakdown[i];
                        if (breakdownItem.bytes) {
                            const breakdownItemTypes = breakdownItem.types;
                            const breakdownItemAttribution = breakdownItem.attribution;
                            let breakdownItemType = "unknown";
                            if (breakdownItemTypes && breakdownItemTypes.length) {
                                breakdownItemType = breakdownItemTypes.join(", ");
                            }
                            const attributes = breakdownItemAttribution ? flattenObject(breakdownItemAttribution) : {};
                            perfData.add({
                                name: metricName + " - breakdown",
                                value: breakdownItem.bytes,
                                unit: PerformanceUnitData.BYTES,
                                attributes: Object.assign(Object.assign(Object.assign(Object.assign({}, attributesFromProvider), customAttributes), attributes), {
                                    breakdownType: breakdownItemType,
                                    pointName: name
                                })
                            });
                        }
                    }
                }
            }), (function() {}));
        }
        if (performance.memory && performance.memory.usedJSHeapSize) {
            const value = performance.memory.usedJSHeapSize;
            const metricName = metricNameBase + "usedJSHeapSize";
            if (value) {
                perfData.add({
                    name: metricName,
                    value: value,
                    unit: PerformanceUnitData.BYTES,
                    attributes: Object.assign(Object.assign(Object.assign({}, attributesFromProvider), customAttributes), {
                        pointName: name
                    })
                });
            }
        }
    }
    destroy() {
        clearTimeout(this.measureTimeout);
    }
}

class TracerPerformanceMemory {
    constructor(sdkKey) {
        this.sdkKey = sdkKey;
    }
    measureMemory(name, attributes) {
        const memoryModule = getSdkInnerModule(this.sdkKey, 6);
        if (memoryModule) {
            memoryModule.measureMemory(name, attributes);
        }
    }
}

function innerInitTracerPerformanceMemory(sdkKey, config = {}) {
    initSdkInnerModule(sdkKey, 5, (function() {
        return new PerformanceData(sdkKey);
    }));
    initSdkInnerModule(sdkKey, 6, (function() {
        return new PerformanceMemoryData(sdkKey, config);
    }));
    return initSdkPublicModule(sdkKey, "memory", (function() {
        return new TracerPerformanceMemory(sdkKey);
    }));
}

function initTracerPerformanceMemory(config = {}) {
    return innerInitTracerPerformanceMemory(mainSdkKey, config);
}

class SessionUploader {
    constructor(sdkKey, options) {
        this.status = "BLANK";
        this.uploaded = false;
        this.beforeUnload = false;
        this.sdkKey = sdkKey;
        this.environment = options.environment;
        this.appToken = options.appToken;
        this.versionName = options.versionName;
        this.versionCode = options.versionCode;
        this.apiHost = options.apiHost;
        this.onBeforeUnload = this.onBeforeUnload.bind(this);
        const sdkData = getSdkInnerModule(this.sdkKey, 1);
        if (sdkData && sdkData.withCrashes()) {
            this.setCrashed(sdkData.getMaxSeverity());
        }
        if (isWindowDefined()) {
            window.addEventListener("beforeunload", this.onBeforeUnload);
        }
    }
    onBeforeUnload() {
        this.beforeUnload = true;
        this.upload();
    }
    setCrashed(maxSeverity) {
        if (this.status === "CRASH") {
            return;
        }
        this.status = "CRASH";
        this.maxSeverity = maxSeverity;
        this.upload();
    }
    destroy() {
        if (typeof window !== "undefined") {
            window.removeEventListener("beforeunload", this.onBeforeUnload);
        }
    }
    upload() {
        if (this.uploaded || isFeatureMuted("trackSession")) {
            return;
        }
        this.uploaded = true;
        const sessionInfo = {
            sessionUuid: getSessionUuid(),
            versionName: this.versionName,
            versionCode: String(this.versionCode),
            status: this.status,
            maxSeverity: toUpperCase(this.maxSeverity),
            environment: this.environment
        };
        this.uploadSessionInfo(sessionInfo);
    }
    uploadSessionInfo(sessionInfo) {
        const requestData = {};
        requestData.versionName = this.versionName;
        requestData.versionCode = String(this.versionCode);
        requestData.deviceId = getDeviceId();
        requestData.sessions = [ sessionInfo ];
        doRequest({
            beforeUnload: this.beforeUnload,
            sdkVersion: getSdkVersion(this.sdkKey),
            apiHost: this.apiHost,
            apiUrl: "/api/crash/trackSession",
            queryParams: {
                crashToken: this.appToken
            },
            data: requestData,
            dataType: "json",
            onMute: muteApiFeature.bind(null, "trackSession")
        });
    }
}

function innerInitTracerSessionUploader(sdkKey, config) {
    initSdkInnerModule(sdkKey, 8, (function() {
        return new SessionUploader(sdkKey, config);
    }));
}

function initTracerSessionUploader(config) {
    return innerInitTracerSessionUploader(mainSdkKey, config);
}

function getTracerModules() {
    return getSdkPublicModules(mainSdkKey);
}

function destroySdkInnerModules(sdkKey) {
    const data = getSdkInnerModules(sdkKey);
    for (const key in data) {
        if (Object.prototype.hasOwnProperty.call(data, key)) {
            const dataItem = data[key];
            if (dataItem) {
                dataItem.destroy();
            }
            data[key] = undefined;
        }
    }
}

function disableTracerModules() {
    destroySdkInnerModules(mainSdkKey);
}

function initTracerSdk(config, ...moduleInit) {
    const sdk = {};
    for (let i = 0, ln = moduleInit.length; i < ln; i++) {
        const module = moduleInit[i](config);
        if (module) {
            const prototype = Object.getPrototypeOf(module);
            const keys = Object.getOwnPropertyNames(prototype);
            for (let i = 0, ln = keys.length; i < ln; i++) {
                const key = keys[i];
                if (typeof prototype[key] === "function" && key !== "constructor") {
                    sdk[key] = prototype[key].bind(module);
                }
            }
        }
    }
    return sdk;
}

function setTracerDeviceId(deviceId) {
    getLocalStorage().setItem(deviceIdLsKey, deviceId);
}

function getTracerDeviceId() {
    return getDeviceId();
}

export { PerformanceUnitData, PerformanceUnitTime, PerformanceUnitValue, disableTracerModules, getTracerDeviceId, getTracerModules, initTracerError, initTracerErrorAsyncStack, initTracerErrorUploader, initTracerLog, initTracerPerformance, initTracerPerformanceMemory, initTracerPerformanceUploader, initTracerSdk, initTracerSessionUploader, initTracerWebVitals, initTracerWebVitalsUploader, setTracerDeviceId };
//# sourceMappingURL=index.mjs.map
